<template>
  <component
    :is="isEmbedMode ? 'div' : 'panel-navigation'"
    v-bind="!isEmbedMode ? { 'default-mini': true, 'mobileEnabled': true } : {}"
  >
    <!-- Location Not Found Dialog -->
    <location-not-found-dialog
      v-model="showLocationNotFoundDialog"
    />

    <!-- Breadcrumb for iframe mode -->
    <div v-if="isEmbedMode" class="breadcrumb-strip">
      <span class="breadcrumb-item clickable" @click="goToScans">All Scans</span>
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-item current">New Scan</span>
    </div>

    <div class="create-scan-container" :class="{ 'with-breadcrumb': isEmbedMode }">
      <!-- Full Page Map -->
      <div ref="googleMap" class="google-map-full"></div>

      <!-- Enable All Message -->
      <div v-if="hasDisabledMarkers" class="enable-all-message" @click="enableAllMarkers">
        <span>Enable all</span>
      </div>

      <!-- Top Left Search Box Overlay - Only show when no business is selected -->
      <div v-if="!selectedBusiness" class="search-overlay">
        <div class="search-card">
          <h3 class="search-title">Target Business</h3>
          
          <!-- Unified Search Input -->
          <div class="search-input-wrapper">
            <label class="search-label">Search Business Name/CID/Maps URL</label>
            <div class="search-input-container">
              <input
                ref="businessSearch"
                v-model="businessSearch"
                type="text"
                class="search-input"
                placeholder="Search by business name / paste CID / maps URL"
                :disabled="valueSerpDetailLoading"
                @focus="handleInputFocus"
                @blur="handleInputBlur"
              />
              <button
                v-if="showSearchButton"
                @click="performValueSerpSearch"
                class="value-serp-search-btn"
                :class="{ 'loading': valueSerpSearchLoading }"
                :disabled="!businessSearch || valueSerpSearchLoading || valueSerpDetailLoading"
                :title="useValueSerp ? 'Search with ValueSERP (for Service Area Businesses)' : 'Search with DataForSEO'"
              >
                <svg v-if="valueSerpSearchLoading" class="spinner" width="16" height="16" viewBox="0 0 16 16">
                  <circle class="path" cx="8" cy="8" r="6" fill="none" stroke-width="2"></circle>
                </svg>
                <span v-else>Search</span>
              </button>
            </div>
            
            <!-- ValueSERP Results Dropdown -->
            <div v-if="showValueSerpDropdown" class="value-serp-results-container">
              <!-- Loading state -->
              <div v-if="valueSerpSearchLoading" class="value-serp-dropdown loading">
                <div class="loading-state">
                  <svg class="spinner large" width="24" height="24" viewBox="0 0 24 24">
                    <circle class="path" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>
                  </svg>
                  <span>Searching for businesses...</span>
                </div>
              </div>
              
              <!-- Search results -->
              <div v-else-if="valueSerpSearchResults.length > 0" class="value-serp-dropdown">
                <!-- <div class="dropdown-header">
                  <span>ValueSERP Results</span>
                  <button @click="closeValueSerpDropdown" class="close-dropdown">
                    <i class="fas fa-times"></i>
                  </button>
                </div> -->
                <div 
                  v-for="(result, index) in valueSerpSearchResults" 
                  :key="result.data_cid || index"
                  @click="selectValueSerpBusiness(result)"
                  class="dropdown-item"
                  :class="{ 'hovered': hoveredIndex === index }"
                  @mouseenter="hoveredIndex = index"
                  @mouseleave="hoveredIndex = -1"
                >
                  <div class="business-icon">
                    <i class="fas fa-map-marker-alt"></i>
                  </div>
                  <div class="business-info">
                    <div class="business-line">
                      <span class="business-title">{{ result.title }}{{ isServiceAreaBusiness(result) ? ' (SAB)' : '' }}</span>
                      <span class="separator"> </span>
                      <span class="business-address">{{ formatValueSerpAddress(result) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- No results -->
              <div v-else class="value-serp-dropdown">
                <div class="no-results">
                  <i class="fas fa-exclamation-circle"></i>
                  <span>No businesses found. Try a different search term.</span>
                </div>
              </div>
            </div>
            
            <!-- Hint text -->
            <div v-if="isValueSerpBusiness" class="search-hint">
              <i class="fas fa-info-circle"></i>
              <span>Service Area Business selected via ValueSERP</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Overlay - Matches form.png -->
      <div v-if="selectedBusiness" class="form-overlay">
        <div class="form-card">
          <!-- Target Business Section with Edit Icon -->
          <div class="target-business-section">
            <div class="target-business-header">
              <div class="header-left">
                <h3 class="target-business-title">Target Business</h3>
                <!-- Show business name when collapsed -->
                <span v-if="isFormCollapsed" class="collapsed-business-name">{{ selectedBusiness.name }}</span>
              </div>
              <div class="header-actions">
                <button v-if="!isEditMode && !isDuplicateMode" class="edit-business-btn" @click="resetBusinessSelection" title="Change business">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.333 2.00004C11.5084 1.82455 11.7163 1.68477 11.9451 1.58848C12.1739 1.49218 12.4191 1.44141 12.6667 1.44141C12.9142 1.44141 13.1594 1.49218 13.3882 1.58848C13.617 1.68477 13.8249 1.82455 14 2.00004C14.1755 2.17552 14.3153 2.38343 14.4116 2.61224C14.5079 2.84105 14.5586 3.08623 14.5586 3.33337C14.5586 3.58052 14.5079 3.8257 14.4116 4.05451C14.3153 4.28332 14.1755 4.49122 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.333 2.00004Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <!-- Mobile Toggle Arrow -->
                <button class="mobile-toggle-btn" @click="isFormCollapsed = !isFormCollapsed">
                  <i :class="['fas', isFormCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                </button>
              </div>
            </div>
            <!-- Business Details with Border - Also hides on toggle -->
            <div v-show="!isFormCollapsed" class="business-details-box">
              <h4 class="business-name">{{ selectedBusiness.name }}{{ selectedBusiness.isServiceAreaBusiness ? ' (SAB)' : '' }}</h4>
              <div class="business-meta">
              <div class="rating-wrapper">
                <div class="stars">
                  <span v-for="i in 5" :key="i" class="star" :class="{ 'filled': i <= Math.round(selectedBusiness.rating || 4.5) }">★</span>
                </div>
                <span class="rating-value">{{ selectedBusiness.rating || '4.5' }} ({{ selectedBusiness.user_ratings_total || '233' }} reviews)</span>
              </div>
              <div class="contact-info">
                <span class="phone-number" style="display: flex; align-items: center;">
                  <svg style="margin-right: 10px;" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.6335 15.1668C10.8802 15.1668 10.0868 14.9868 9.26683 14.6402C8.46683 14.3002 7.66016 13.8335 6.8735 13.2668C6.0935 12.6935 5.34016 12.0535 4.62683 11.3535C3.92016 10.6402 3.28016 9.88683 2.7135 9.1135C2.14016 8.3135 1.68016 7.5135 1.3535 6.74016C1.00683 5.9135 0.833496 5.1135 0.833496 4.36016C0.833496 3.84016 0.926829 3.34683 1.10683 2.88683C1.2935 2.4135 1.5935 1.9735 2.00016 1.5935C2.5135 1.08683 3.10016 0.833496 3.72683 0.833496C3.98683 0.833496 4.2535 0.893496 4.48016 1.00016C4.74016 1.12016 4.96016 1.30016 5.12016 1.54016L6.66683 3.72016C6.80683 3.9135 6.9135 4.10016 6.98683 4.28683C7.0735 4.48683 7.12016 4.68683 7.12016 4.88016C7.12016 5.1335 7.04683 5.38016 6.90683 5.6135C6.80683 5.7935 6.6535 5.98683 6.46016 6.18016L6.00683 6.6535C6.0135 6.6735 6.02016 6.68683 6.02683 6.70016C6.10683 6.84016 6.26683 7.08016 6.5735 7.44016C6.90016 7.8135 7.20683 8.1535 7.5135 8.46683C7.90683 8.8535 8.2335 9.16016 8.54016 9.4135C8.92016 9.7335 9.16683 9.8935 9.3135 9.96683L9.30016 10.0002L9.78683 9.52016C9.9935 9.3135 10.1935 9.16016 10.3868 9.06016C10.7535 8.8335 11.2202 8.7935 11.6868 8.98683C11.8602 9.06016 12.0468 9.16016 12.2468 9.30016L14.4602 10.8735C14.7068 11.0402 14.8868 11.2535 14.9935 11.5068C15.0935 11.7602 15.1402 11.9935 15.1402 12.2268C15.1402 12.5468 15.0668 12.8668 14.9268 13.1668C14.7868 13.4668 14.6135 13.7268 14.3935 13.9668C14.0135 14.3868 13.6002 14.6868 13.1202 14.8802C12.6602 15.0668 12.1602 15.1668 11.6335 15.1668ZM3.72683 1.8335C3.36016 1.8335 3.02016 1.9935 2.6935 2.3135C2.38683 2.60016 2.1735 2.9135 2.04016 3.2535C1.90016 3.60016 1.8335 3.96683 1.8335 4.36016C1.8335 4.98016 1.98016 5.6535 2.2735 6.34683C2.5735 7.0535 2.9935 7.78683 3.52683 8.52016C4.06016 9.2535 4.66683 9.96683 5.3335 10.6402C6.00016 11.3002 6.72016 11.9135 7.46016 12.4535C8.18016 12.9802 8.92016 13.4068 9.6535 13.7135C10.7935 14.2002 11.8602 14.3135 12.7402 13.9468C13.0802 13.8068 13.3802 13.5935 13.6535 13.2868C13.8068 13.1202 13.9268 12.9402 14.0268 12.7268C14.1068 12.5602 14.1468 12.3868 14.1468 12.2135C14.1468 12.1068 14.1268 12.0002 14.0735 11.8802C14.0535 11.8402 14.0135 11.7668 13.8868 11.6802L11.6735 10.1068C11.5402 10.0135 11.4202 9.94683 11.3068 9.90016C11.1602 9.84016 11.1002 9.78016 10.8735 9.92016C10.7402 9.98683 10.6202 10.0868 10.4868 10.2202L9.98016 10.7202C9.72016 10.9735 9.32016 11.0335 9.0135 10.9202L8.8335 10.8402C8.56016 10.6935 8.24016 10.4668 7.88683 10.1668C7.56683 9.8935 7.22016 9.5735 6.80016 9.16016C6.4735 8.82683 6.14683 8.4735 5.80683 8.08016C5.4935 7.7135 5.26683 7.40016 5.12683 7.14016L5.04683 6.94016C5.00683 6.78683 4.9935 6.70016 4.9935 6.60683C4.9935 6.36683 5.08016 6.1535 5.24683 5.98683L5.74683 5.46683C5.88016 5.3335 5.98016 5.20683 6.04683 5.0935C6.10016 5.00683 6.12016 4.9335 6.12016 4.86683C6.12016 4.8135 6.10016 4.7335 6.06683 4.6535C6.02016 4.54683 5.94683 4.42683 5.8535 4.30016L4.30683 2.1135C4.24016 2.02016 4.16016 1.9535 4.06016 1.90683C3.9535 1.86016 3.84016 1.8335 3.72683 1.8335ZM9.30016 10.0068L9.1935 10.4602L9.3735 9.9935C9.34016 9.98683 9.3135 9.9935 9.30016 10.0068Z" fill="#19233A" fill-opacity="0.5"/>
                    </svg>
                    {{ selectedBusiness.phone || '+1 520-601-2749' }}</span>
              </div>
              <div class="address-info">
                <span class="address"  style="display: flex; align-items: center;">
                  <svg style="margin-right: 10px;" width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.66699 0.400391C8.17369 0.402243 9.61996 0.956453 10.6875 1.94434C11.7553 2.93248 12.3574 4.27456 12.3594 5.67578V5.67676C12.3462 6.69341 12.0295 7.68659 11.4453 8.5459C10.8604 9.44629 9.75122 11.0109 8.78516 12.3545C8.30152 13.0271 7.85359 13.6452 7.52441 14.0977C7.35995 14.3237 7.22455 14.508 7.12988 14.6377C7.08273 14.7023 7.04545 14.7536 7.01953 14.7891C7.00656 14.8068 6.99637 14.8213 6.98926 14.8311C6.98571 14.8359 6.98246 14.84 6.98047 14.8428C6.97962 14.8439 6.97902 14.845 6.97852 14.8457L6.97754 14.8467L6.97656 14.8486C6.90494 14.9451 6.78881 14.9999 6.66797 15C6.54721 15 6.43105 14.945 6.35938 14.8486L6.34082 14.8242L6.33984 14.8223C6.07395 14.4582 3.01584 10.2725 1.8877 8.54395L1.68164 8.21582C1.22976 7.43673 0.986428 6.56566 0.974609 5.67676V5.67578C0.976595 4.27455 1.57877 2.93258 2.64648 1.94434C3.71393 0.956388 5.16027 0.402228 6.66699 0.400391ZM6.66699 1.10938C5.35441 1.11114 4.09695 1.59424 3.1709 2.45117C2.24528 3.30788 1.72562 4.46785 1.72363 5.67578C1.7418 6.5576 2.02413 7.41778 2.53809 8.16211L2.54102 8.16699C3.48221 9.62079 5.82769 12.8539 6.67285 14.0146C7.51263 12.8574 9.85899 9.6214 10.8076 8.16699L10.8086 8.16504C11.3184 7.41879 11.5955 6.55725 11.6094 5.67578L11.6025 5.4502C11.5405 4.32427 11.03 3.25442 10.1621 2.45117C9.23618 1.59444 7.9794 1.11122 6.66699 1.10938Z" fill="#19233A" stroke="#19233A" stroke-width="0.2"/>
                    <path d="M6.66699 3.15381C7.38402 3.1539 8.07296 3.41697 8.58105 3.88721C9.08943 4.35774 9.375 4.99736 9.375 5.66455C9.37492 6.33147 9.08926 6.97047 8.58105 7.44092C8.07296 7.91115 7.38402 8.1752 6.66699 8.17529C5.95013 8.17438 5.26187 7.90998 4.75391 7.43994C4.24577 6.96975 3.95906 6.33126 3.95801 5.66455C3.95801 4.99733 4.2445 4.35774 4.75293 3.88721C5.26108 3.41709 5.94999 3.15381 6.66699 3.15381ZM6.66699 3.76221C6.11992 3.76261 5.5957 3.96486 5.20996 4.32178C4.82449 4.67863 4.60882 5.16175 4.6084 5.66455C4.60849 6.16743 4.82434 6.65044 5.20996 7.00732C5.59579 7.36431 6.1199 7.56592 6.66699 7.56592C7.21403 7.56582 7.73828 7.36433 8.12402 7.00732C8.50949 6.65046 8.72549 6.16733 8.72559 5.66455C8.72559 5.1615 8.50965 4.67775 8.12402 4.3208C7.73827 3.96387 7.21403 3.7623 6.66699 3.76221Z" fill="black" stroke="black" stroke-width="0.1"/>
                  </svg>

                  {{ selectedBusiness.address || selectedBusiness.country || 'Service Area Business' }}</span>
              </div>
              <!-- Opening hours removed due to Google API deprecation -->
            </div>
            </div>
          </div>

          <!-- Collapsible Form Content -->
          <div v-show="!isFormCollapsed" class="form-content">
          <!-- Settings Section -->
          <div class="form-section">
            <h4 class="section-header">Settings</h4>
            
            <!-- Keywords with Border -->
            <div class="keyword-section">
              <label class="field-label">Keyword</label>
              <input
                v-model="keywordInput"
                @keyup.enter="addKeyword"
                type="text"
                class="field-input"
                placeholder="Enter keyword and press Enter"
              />
              <small class="field-hint">High intent keywords for your business</small>
              <div class="keyword-tags">
                <span v-for="(keyword, index) in keywords" :key="'keyword-' + index" class="keyword-tag">
                  {{ keyword }}
                  <span class="remove-tag" @click="removeKeyword(index)">×</span>
                </span>
              </div>

              <!-- Suggested Keywords Section -->
              <div v-if="suggestedKeywords.length > 0 || loadingSuggestions" class="suggested-keywords-section">
                <div class="suggested-keywords-header">
                  <span class="suggested-label">Suggested Keywords</span>
                  <svg v-if="loadingSuggestions" class="spinner small" width="14" height="14" viewBox="0 0 16 16">
                    <circle class="path" cx="8" cy="8" r="6" fill="none" stroke-width="2"></circle>
                  </svg>
                </div>
                <div v-if="!loadingSuggestions" class="suggested-keyword-tags">
                  <span
                    v-for="(keyword, index) in visibleSuggestions"
                    :key="'suggested-' + index"
                    class="suggested-keyword-tag"
                    @click="addSuggestedKeyword(keyword)"
                    title="Click to add this keyword"
                  >
                    <span class="add-icon">+</span>
                    {{ keyword }}
                  </span>
                  <span
                    v-if="hasMoreSuggestions"
                    class="suggested-keyword-tag more-button"
                    @click="toggleShowAllSuggestions"
                    title="Show more suggestions"
                  >
                    More
                  </span>
                  <span
                    v-else-if="showAllSuggestions && suggestedKeywords.length > 2"
                    class="suggested-keyword-tag more-button"
                    @click="toggleShowAllSuggestions"
                    title="Show less suggestions"
                  >
                    Less
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Map Grid Section with Border - Contains all grid-related controls -->
          <h4 class="section-header">Map Grid</h4>
          <div class="map-grid-section">
            
            <!-- First Row: Grid Type and Grid Size (50-50) -->
            <div class="form-row">
              <div class="form-field">
                <select v-model="gridType" @change="setGridType(gridType)" class="field-select">
                  <option value="square">◻ Square</option>
                  <option value="circle">○ Circle</option>
                </select>
              </div>
              <div class="form-field">
                <select v-model="gridSize" @change="drawGrid" class="field-select">
                  <option value="3x3">9 pins | 3X3</option>
                  <option value="4x4">16 pins | 4X4</option>
                  <option value="5x5">25 pins | 5X5</option>
                  <option value="6x6">36 pins | 6X6</option>
                  <option value="7x7">49 pins | 7X7</option>
                  <option value="8x8">64 pins | 8X8</option>
                  <option value="9x9">81 pins | 9X9</option>
                  <option value="10x10">100 pins | 10X10</option>
                  <option value="11x11">121 pins | 11X11</option>
                  <option value="12x12">144 pins | 12X12</option>
                  <option value="13x13">169 pins | 13X13</option>
                  <option value="14x14">196 pins | 14X14</option>
                  <option value="15x15">225 pins | 15X15</option>
                  <option value="16x16">256 pins | 16X16</option>
                  <option value="17x17">289 pins | 17X17</option>
                  <option value="18x18">324 pins | 18X18</option>
                  <option value="19x19">361 pins | 19X19</option>
                  <option value="20x20">400 pins | 20X20</option>
                  <option value="21x21">441 pins | 21X21</option>
                </select>
              </div>
            </div>

            <!-- Second Row: KM/Miles Toggle (30%) and Radius Dropdown (70%) -->
            <div class="radius-row">
              <div class="unit-toggle-container">
                <div class="segmented-toggle">
                  <button 
                    class="toggle-segment" 
                    :class="{ active: useKilometers }"
                    @click="useKilometers = true; toggleDistanceUnit()"
                  >
                    KM
                  </button>
                  <button 
                    class="toggle-segment" 
                    :class="{ active: !useKilometers }"
                    @click="useKilometers = false; toggleDistanceUnit()"
                  >
                    Miles
                  </button>
                </div>
              </div>
              <div class="radius-select-container">
                <label class="field-label">Distance between pins</label>
                <select v-model="radiusValue" @change="updateRadius" class="field-select">
                  <option :value="0.1">0.1 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="0.2">0.2 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="0.5">0.5 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="1">1 {{ useKilometers ? 'KM' : 'Mile' }}</option>
                  <option :value="1.5">1.5 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="2.5">2.5 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="5">5 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="10">10 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="25">25 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                  <option :value="30">30 {{ useKilometers ? 'KM' : 'Miles' }}</option>
                </select>
              </div>
            </div>

            <!-- Location Count Display -->
            <div class="location-info-row">
              <span class="location-count">{{ gridSize }} Grid ({{ gridPoints.length }} Locations)</span>
            </div>
          </div>

          <!-- Schedule Section -->
          <h4 class="section-header">Schedule</h4>
          <div class="form-section schedule-grid-section">
            
            <div class="form-field">
              <label class="field-label">Scan Type</label>
              <select v-model="scanType" @change="onScanTypeChange" class="field-select">
                <option value="one-time">One Time</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>

            <!-- Recurring Options -->
            <div v-if="scanType === 'recurring'" class="recurring-fields">
              <div class="form-field">
                <label class="field-label">Grid Name</label>
                <input v-model="gridName" type="text" class="field-input" placeholder="Enter grid name">
              </div>
              
              <div class="form-field">
                <label class="field-label">Scan Frequency</label>
                <select v-model="scanFrequency" class="field-select">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="manual">Manual</option>
                </select>
              </div>

              <div v-if="scanFrequency !== 'manual'" class="form-field">
                <label class="field-label">Preferred Time</label>
                <select v-model="scanTime" class="field-select">
                  <option value="morning">Morning (9 AM)</option>
                  <option value="afternoon">Afternoon (2 PM)</option>
                  <option value="evening">Evening (6 PM)</option>
                  <option value="night">Night (9 PM)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            @click="startScan"
            class="submit-button"
            :disabled="loading || keywords.length === 0"
          >
            <span v-if="!loading">{{ isEditMode ? 'UPDATE SCAN' : 'START SCAN' }}</span>
            <span v-else>{{ isEditMode ? 'Updating...' : 'Processing...' }}</span>
          </button>
          </div><!-- End of collapsible form content -->
        </div>
      </div>

      <!-- Success Modal - Matches backend/popup.png -->
      <v-dialog
        v-model="showSuccessModal"
        :max-width="$vuetify.breakpoint.mobile ? '100%' : '600'"
        :fullscreen="false"
        persistent
      >
        <v-card class="success-modal-card">
          <v-btn icon absolute right top @click="showSuccessModal = false" class="close-btn">
            <v-icon color="grey">mdi-close</v-icon>
          </v-btn>
          
          <v-card-text class="text-center pa-8">
            <div class="success-icon-container">
              <img src="@/assets/tick.png" alt="Success" width="80" height="80" class="success-icon" />
            </div>
            
            <h2 class="success-title">Your Scan has started</h2>
            
            <p class="success-message">
              Your business scan is in progress. Results will be available<br>
              in few mins. Check results anytime.
            </p>
            
            <div class="action-buttons">
              <v-btn 
                outlined 
                @click="viewHistory" 
                class="action-btn outlined-btn"
              >
                View History
              </v-btn>
              <v-btn 
                outlined
                @click="startNewScan" 
                class="action-btn outlined-btn"
              >
                Start New Scan
              </v-btn>
              <v-btn
                depressed
                @click="viewReport"
                class="action-btn primary-btn"
              >
                View Grid
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </component>
</template>

<script>
import PanelNavigation from '@/components/PanelNavigation.vue'
import LocationNotFoundDialog from '@/components/LocationNotFoundDialog.vue'
import embedMode from '@/mixins/embedMode'
import axios from 'axios'
import { mapState } from 'vuex'
import { trackUserJourney } from '@/utils/userJourney'

export default {
  metaInfo () {
    return {
      title: "Create Scan | " + this.$appName
    }
  },
  name: 'CreateScan',
  mixins: [embedMode],
  components: {
    PanelNavigation,
    LocationNotFoundDialog
  },
  data() {
    return {
      // Map related
      map: null,
      autocomplete: null,
      businessMarker: null,
      businessMarkerLabel: null,
      gridRectangle: null,
      gridCircle: null,
      gridMarkers: [],
      gridPoints: [],
      disabledMarkers: [], // Track disabled marker indices as array for reactivity
      hoveredMarkerIndex: null, // Track which marker is being hovered
      gridCenter: null,
      googleMapsLoaded: false,

      // Form data
      businessSearch: '',
      selectedBusiness: null,
      keywords: [],
      showLocationNotFoundDialog: false,
      isLoadingPlaceDetails: false, // Prevents popup while loading place details
      keywordInput: '',
      suggestedKeywords: [],
      loadingSuggestions: false,
      showAllSuggestions: false,
      gridSize: '3x3',
      radiusValue: 1,
      useKilometers: false,
      gridType: 'square',
      scanType: 'one-time',
      gridName: '',
      scanFrequency: 'daily',
      scanTime: 'morning',
      loading: false,

      // Edit mode
      isEditMode: false,
      editingGridId: null,

      // Value SERP fields
      searchProvider: 'google_places',
      valueSerpSearchResults: [],
      valueSerpSearchLoading: false,
      showValueSerpDropdown: false,
      hoveredIndex: -1,
      valueSerpDetailLoading: false,

      // Search configuration
      useValueSerp: true, // Default to true, will be updated from backend
      searchConfig: null,

      // Modal
      showSuccessModal: false,
      lastSearchId: null,

      // Mobile toggle state
      isFormCollapsed: false
    }
  },
  computed: {
    ...mapState('apiKeys', ['googleMaps']),
    hasDisabledMarkers() {
      return this.disabledMarkers.length > 0;
    },
    isGoogleMapsConfigured() {
      return this.googleMaps.apiKey && this.googleMaps.apiKey.trim() !== '';
    },
    isGoogleMapsValid() {
      return this.isGoogleMapsConfigured && this.googleMaps.isValid;
    },
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    embedToken() {
      return this.$store.getters['embed/embedToken'];
    },
    showSearchButton() {
      // Show search button if:
      // 1. ValueSerp is enabled (useValueSerp = true), OR
      // 2. DataForSEO search is enabled (when useValueSerp = false and dataForSeoSearchEnabled = true)
      if (this.searchConfig) {
        return this.searchConfig.showSearchButton !== false;
      }
      // Default to showing if config hasn't loaded yet
      return true;
    },
    radius() {
      // Return the radius value as-is (no conversion needed)
      // The backend will handle the conversion to meters based on distance_type
      return this.radiusValue.toString();
    },
    radiusUnit() {
      return this.useKilometers ? 'km' : 'miles';
    },
    isValueSerpBusiness() {
      return this.selectedBusiness && this.selectedBusiness.isValueSerp;
    },
    isDuplicateMode() {
      return !!this.$route.query.duplicateFrom;
    },
    visibleSuggestions() {
      // Show only first 2 suggestions initially, all when showAllSuggestions is true
      if (this.showAllSuggestions || this.suggestedKeywords.length <= 2) {
        return this.suggestedKeywords;
      }
      return this.suggestedKeywords.slice(0, 2);
    },
    hasMoreSuggestions() {
      // Show "More" button if there are more than 2 suggestions and not all are shown
      return this.suggestedKeywords.length > 2 && !this.showAllSuggestions;
    }
  },
  watch: {
    gridSize() {
      // Only redraw grid if we have both a selected business and a marker on the map
      if (this.selectedBusiness && this.businessMarker) {
        this.drawGrid();
      }
    },
    businessSearch(newVal) {
      // When search input is cleared, close ValueSERP dropdown and re-enable Google
      if (!newVal || newVal.trim() === '') {
        if (this.showValueSerpDropdown) {
          this.closeValueSerpDropdown();
        }
      }
    }
  },
  async mounted() {
    // Track embed usage if in embedded mode
    if (this.isEmbedMode) {
      this.trackEmbedUsage('create-scan');
    }

    // Fetch search configuration first
    await this.fetchSearchConfig();

    // Fetch the latest Google Maps API key status from backend
    // This works in both normal and embedded modes as it uses the auth token
    await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');

    // Check if we're in edit or duplicate mode
    const isEditMode = this.$route.query.mode === 'edit' && this.$route.query.editId;
    const isDuplicateMode = this.$route.query.duplicateFrom;

    // Skip API key check if editing or duplicating (scan was already created with valid key)
    if (!isEditMode && !isDuplicateMode && !this.isGoogleMapsValid) {
      // Check if in embed mode
      if (this.$store.getters['embed/isEmbedMode']) {
        // In embed mode, show dialog with option to go to parent website settings
        this.$swal({
          icon: 'info',
          title: 'API Key Not Configured',
          text: 'Google Maps API key is not configured. Please go to the settings page to integrate the API.',
          confirmButtonText: 'Go to Settings Page',
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(() => {

          // Open parent website's settings page in a new tab
          // window.open('/settings', '_blank');
          this.$router.push('/scans');
        });
      } else {
        // Normal mode - show full settings option
        this.$swal({
          icon: 'warning',
          title: 'Google Maps API Key Required',
          text: !this.isGoogleMapsConfigured
            ? 'Please configure your Google Maps API key in Settings to use this feature.'
            : 'Your Google Maps API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          } else {
            this.$router.push('/scans');
          }
        });
      }
      return;
    }

    await this.loadGoogleMapsAPI();
    this.initMap();

    // Add a small delay to ensure Places library is fully loaded
    // This is especially important in iframe mode where loading can be async
    // Increase delay for iframe mode to ensure full library load
    const delay = this.isEmbedMode ? 500 : 100;
    await new Promise(resolve => setTimeout(resolve, delay));
    this.initAutocomplete();
    
    // Check if we're editing an existing scan
    if (this.$route.query.mode === 'edit' && this.$route.query.editId) {
      this.isEditMode = true;
      this.editingGridId = this.$route.query.editId;
      await this.loadEditData(this.$route.query.editId);
    }
    // Check if we're duplicating an existing scan
    else if (this.$route.query.duplicateFrom) {
      await this.loadDuplicateData(this.$route.query.duplicateFrom);
    }
    
    // Add click outside handler for ValueSERP dropdown
    this.handleClickOutside = (event) => {
      if (this.showValueSerpDropdown) {
        const searchContainer = this.$el.querySelector('.search-container');
        const valueSerpDropdown = this.$el.querySelector('.value-serp-results-container');
        
        if (searchContainer && valueSerpDropdown && 
            !searchContainer.contains(event.target) && 
            !valueSerpDropdown.contains(event.target)) {
          this.closeValueSerpDropdown();
        }
      }
    };
    
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    // Clean up event listener
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }

    // Re-enable Google autocomplete if needed
    this.enableGoogleAutocomplete();
  },
  
  methods: {
    goToScans() {
      this.$router.push('/scans');
    },
    async fetchSearchConfig() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/config`,
          {
            headers: {
              'Authorization': 'Bearer ' + (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])
            }
          }
        );

        if (response.data.success) {
          this.searchConfig = response.data;
          this.useValueSerp = response.data.useValueSerp;
          // Set default search provider based on config
          if (!this.useValueSerp) {
            this.searchProvider = 'google_places';
          }
        }
      } catch (error) {
        console.error('Failed to fetch search config:', error);
        // Continue with defaults if config fetch fails
      }
    },

    async loadGoogleMapsAPI() {
      if (window.google && window.google.maps) {
        this.googleMapsLoaded = true;
        return;
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMaps.apiKey}&libraries=places,drawing,geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.googleMapsLoaded = true;
          resolve();
        };
        document.head.appendChild(script);
      });
    },

    initMap() {
      if (!this.$refs.googleMap || !window.google) return;
      
      const mapCenter = { lat: 33.5186, lng: -86.8104 };
      
      this.map = new window.google.maps.Map(this.$refs.googleMap, {
        zoom: 12,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER
        },
        mapTypeControl: false, // Disabled map type control
        scaleControl: true,
        streetViewControl: false, // Disabled street view control (Pegman)
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      // Add custom home/center control
      this.addHomeControl();
    },

    addHomeControl() {
      // Create the home control button
      const homeControlDiv = document.createElement('div');

      // Set CSS for the control border
      const controlUI = document.createElement('div');
      controlUI.style.backgroundColor = '#fff';
      controlUI.style.border = '2px solid #fff';
      controlUI.style.borderRadius = '3px';
      controlUI.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
      controlUI.style.cursor = 'pointer';
      controlUI.style.marginRight = '10px';
      controlUI.style.marginTop = '10px';
      controlUI.style.textAlign = 'center';
      controlUI.title = 'Click to recenter the map';
      homeControlDiv.appendChild(controlUI);

      // Set CSS for the control interior
      const controlText = document.createElement('div');
      controlText.style.color = 'rgb(25,25,25)';
      controlText.style.fontFamily = 'Roboto,Arial,sans-serif';
      controlText.style.fontSize = '16px';
      controlText.style.lineHeight = '38px';
      controlText.style.paddingLeft = '12px';
      controlText.style.paddingRight = '12px';
      controlText.innerHTML = '⌂';
      controlUI.appendChild(controlText);

      // Setup the click event listeners
      controlUI.addEventListener('click', () => {
        const centerToUse = this.gridCenter || { lat: 33.5186, lng: -86.8104 };
        this.map.setCenter(centerToUse);
        this.map.setZoom(14);
      });

      homeControlDiv.index = 1;
      this.map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(homeControlDiv);
    },

    initAutocomplete() {
      if (!this.$refs.businessSearch || !window.google) return;

      // Check if places library is loaded
      if (!window.google.maps || !window.google.maps.places) {
        console.warn('Google Places library not loaded yet. Retrying...');
        // Retry after a delay - places library might still be loading
        setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            this.initAutocomplete();
          } else {
            console.error('Google Places library failed to load');
          }
        }, 500);
        return;
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.businessSearch,
        {
          types: ['establishment'],
          // Request ALL available fields from Google Places API
          fields: [
            'place_id', 
            'name', 
            'formatted_address', 
            'geometry', 
            'rating', 
            'user_ratings_total', 
            'formatted_phone_number',
            'international_phone_number',
            'website',
            'url',
            'types',
            'opening_hours',
            'price_level',
            'business_status',
            'icon',
            'photos',
            'reviews',
            'vicinity',
            'plus_code'
          ]
        }
      );
      
      this.autocomplete.addListener('place_changed', () => {
        // Improved retry logic to handle Google API loading delays
        // This is especially important in embed/iframe mode
        const checkPlaceData = (retryCount = 0, maxRetries = 4) => {
          const place = this.autocomplete.getPlace();

          // Debug logging (can be removed in production)
          // console.log(`[Attempt ${retryCount + 1}] Place data:`, {
          //   hasPlace: !!place,
          //   hasPlaceId: !!(place && place.place_id),
          //   hasGeometry: !!(place && place.geometry),
          //   hasLocation: !!(place && place.geometry && place.geometry.location),
          //   placeName: place ? place.name : undefined
          // });

          // CRITICAL CHECK: If no place object at all or no place_id,
          // user didn't actually select from dropdown (might have pressed Enter)
          if (!place || !place.place_id) {
            // Don't retry if user didn't make a valid selection
            // console.log('No valid place selection - user may have pressed Enter or clicked outside');
            // Reset loading state
            this.isLoadingPlaceDetails = false;
            // Don't show error for this - just ignore the event
            return;
          }

          // GOOD: User selected from dropdown (has place_id)
          // Set loading state to prevent popup during retries
          if (retryCount === 0) {
            this.isLoadingPlaceDetails = true;
            // console.log('🔄 Started loading place details...');
          }

          // Now check if geometry data is loaded
          if (place.geometry && place.geometry.location) {
            // SUCCESS! Data is complete - process it
            // console.log('✅ Place data complete - processing');
            this.isLoadingPlaceDetails = false; // Clear loading state
            this.processGooglePlaceSelection(place);
            return;
          }

          // Place was selected but geometry not loaded yet
          // This is the timing issue - need to retry
          if (retryCount < maxRetries) {
            // Progressive delays: 0ms, 150ms, 250ms, 400ms, 600ms
            // First retry is immediate (sometimes data loads quickly)
            // Subsequent retries have increasing delays
            const delays = [0, 150, 250, 400, 600];
            const delay = delays[retryCount] || 600;

            // console.log(`⏱️ Geometry not loaded yet - retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);

            setTimeout(() => checkPlaceData(retryCount + 1, maxRetries), delay);
            return;
          }

          // All retries exhausted and still no geometry
          // This is a real error - Google API failed to load place details
          console.error('❌ Failed to load place details after all retries');
          console.error('Place object:', place);

          // Clear loading state
          this.isLoadingPlaceDetails = false;

          // Show error dialog only if user actually typed something AND not currently loading
          if (this.businessSearch && this.businessSearch.trim().length > 0) {
            this.showLocationNotFoundDialog = true;
          }
        };

        // Start checking with first attempt (0ms delay on first try)
        checkPlaceData();
      });
    },

    processGooglePlaceSelection(place) {
      // Capture ALL fields from Google Places API
        this.selectedBusiness = {
          // Basic info
          name: place.name || '',
          address: place.formatted_address || '',
          formatted_address: place.formatted_address || '',
          placeId: place.place_id,
          place_id: place.place_id,

          // Location fields - extract from address components if available
          country: this.extractCountryFromPlace(place) || 'United States',
          city: this.extractCityFromPlace(place) || null,
          state: this.extractStateFromPlace(place) || null,
          postal_code: this.extractPostalCodeFromPlace(place) || null,
          
          // Location
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          },
          geometry: place.geometry,
          
          // Contact & Rating
          rating: place.rating,
          user_ratings_total: place.user_ratings_total,
          phone: place.formatted_phone_number,
          formatted_phone_number: place.formatted_phone_number,
          international_phone_number: place.international_phone_number,
          
          // Business details
          website: place.website,
          url: place.url,
          icon: place.icon,
          business_status: place.business_status,
          
          // Categories/Types
          types: place.types || [],
          primary_category: place.types && place.types[0] ? place.types[0] : '',
          
          // Hours & pricing
          opening_hours: place.opening_hours,
          price_level: place.price_level,
          
          // Additional info with properly formatted photos
          photos: place.photos ? place.photos.slice(0, 5).map(photo => {
            // Get photo URL and reference for storage
            const photoData = {
              height: photo.height,
              width: photo.width,
              html_attributions: photo.html_attributions || []
            };
            
            // Try to get the URL (for immediate use)
            if (photo.getUrl) {
              photoData.url = photo.getUrl({maxWidth: 400});
            }
            
            // Note: photo_reference is not available in JS API, only in web service API
            // We'll use the URL instead
            return photoData;
          }) : [],
          reviews: place.reviews,
          vicinity: place.vicinity,
          plus_code: place.plus_code,
          
          // All raw data
          raw_google_data: place,
          
          // Source identifier
          source: 'google_places'
        };
        
        // Set search provider to google_places for proper saving
        this.searchProvider = 'google_places';
        
        this.gridCenter = this.selectedBusiness.location;
        
        // Center map on business (don't zoom in too much initially)
        this.map.setCenter(this.selectedBusiness.location);
        this.map.setZoom(13);
        
        // Add business marker with custom blue pin
        if (this.businessMarker) {
          this.businessMarker.setMap(null);
        }
        
        // Use custom draggable marker with cursor icon for business location
        this.businessMarker = new window.google.maps.Marker({
          position: this.selectedBusiness.location,
          map: this.map,
          title: this.selectedBusiness.name,
          icon: this.getDraggableMarkerIcon(),
          draggable: true,
          animation: window.google.maps.Animation.DROP,
          zIndex: 1000
        });
        
        // Add drag listeners to the business marker
        this.businessMarker.addListener('drag', (event) => {
          this.gridCenter = { 
            lat: event.latLng.lat(), 
            lng: event.latLng.lng() 
          };
          this.updateGridWhileDragging();
          this.updateDragLabel();
        });
        
        this.businessMarker.addListener('dragend', (event) => {
          this.gridCenter = { 
            lat: event.latLng.lat(), 
            lng: event.latLng.lng() 
          };
          // Update the selected business location
          this.selectedBusiness.location = this.gridCenter;
          this.drawGrid();
        });
        
        // Draw initial grid
        this.drawGrid();

        // Add drag label after grid is created
        this.createDragLabel();

        // Fetch keyword suggestions for the selected business
        this.fetchKeywordSuggestions();
    },

    getCustomMarkerIcon() {
      // Custom blue marker with updated colors
      const svg = `
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26c0-8.8-7.2-16-16-16z" fill="#2477F6" stroke="white" stroke-width="1.5"/>
          <circle cx="16" cy="16" r="6" fill="#135CCB"/>
        </svg>
      `;

      const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

      return {
        url: url,
        scaledSize: new window.google.maps.Size(32, 42),
        anchor: new window.google.maps.Point(16, 42)
      };
    },

    getDisabledMarkerIcon() {
      // Disabled marker with gray color
      const svg = `
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26c0-8.8-7.2-16-16-16z" fill="#989898" stroke="white" stroke-width="1.5"/>
          <circle cx="16" cy="16" r="6" fill="#7a7a7a"/>
          <path d="M12 12 L20 20 M20 12 L12 20" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `;

      const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

      return {
        url: url,
        scaledSize: new window.google.maps.Size(32, 42),
        anchor: new window.google.maps.Point(16, 42)
      };
    },

    getMarkerIconWithCross(isDisabled = false) {
      // Marker with white cross on hover
      const fillColor = isDisabled ? '#989898' : '#2477F6';
      const innerColor = isDisabled ? '#7a7a7a' : '#135CCB';

      const svg = `
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26c0-8.8-7.2-16-16-16z" fill="${fillColor}" stroke="white" stroke-width="1.5"/>
          <circle cx="16" cy="16" r="6" fill="${innerColor}"/>
          <path d="M12 12 L20 20 M20 12 L12 20" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `;

      const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

      return {
        url: url,
        scaledSize: new window.google.maps.Size(32, 42),
        anchor: new window.google.maps.Point(16, 42)
      };
    },

    getDraggableMarkerIcon() {
      // Custom blue marker with mdi-cursor-move icon for draggable center
      const svg = `
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26c0-8.8-7.2-16-16-16z" fill="#2477F6" stroke="white" stroke-width="1.5"/>
          <!-- MDI cursor-move icon paths with thinner strokes -->
          <g transform="translate(16, 16)">
            <path d="M 0,-6 L 0,6 M -6,0 L 6,0" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
            <path d="M 0,-6 L -2,-4 M 0,-6 L 2,-4" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M 0,6 L -2,4 M 0,6 L 2,4" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M -6,0 L -4,-2 M -6,0 L -4,2" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M 6,0 L 4,-2 M 6,0 L 4,2" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
        </svg>
      `;
      
      const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
      
      return {
        url: url,
        scaledSize: new window.google.maps.Size(32, 42),
        anchor: new window.google.maps.Point(16, 42)
      };
    },

    createDragLabel() {
      // Don't create label if marker doesn't exist yet
      if (!this.businessMarker) {
        return;
      }
      
      // Remove existing label if any
      if (this.businessMarkerLabel) {
        this.businessMarkerLabel.setMap(null);
      }

      // Calculate the bounds of all grid points to position label above the entire grid
      let bounds = new window.google.maps.LatLngBounds();
      
      // Include center marker
      bounds.extend(this.businessMarker.getPosition());
      
      // Include all grid points
      this.gridPoints.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
      
      // Get the top-center position of the grid
      const topCenter = {
        lat: bounds.getNorthEast().lat(),
        lng: bounds.getCenter().lng()
      };

      // Create custom overlay for the label
      const labelContent = `
        <div style="
          background-color: #19233A;
          color: white;
          padding: 12px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        ">
          <i class="mdi mdi-cursor-move" style="font-size: 16px;"></i>
          HOLD AND DRAG TO MOVE THE GRID
        </div>
      `;

      // Create a custom overlay class for the label
      class MarkerLabel extends window.google.maps.OverlayView {
        constructor(position, content, map, gridPoints, centerPosition) {
          super();
          this.position = position;
          this.content = content;
          this.gridPoints = gridPoints;
          this.centerPosition = centerPosition;
          this.setMap(map);
        }

        onAdd() {
          const div = document.createElement('div');
          div.style.position = 'absolute';
          div.innerHTML = this.content;
          this.div = div;
          const panes = this.getPanes();
          panes.overlayMouseTarget.appendChild(div);
        }

        draw() {
          const overlayProjection = this.getProjection();
          const position = overlayProjection.fromLatLngToDivPixel(this.position);
          if (this.div) {
            this.div.style.left = (position.x - 120) + 'px'; // Center the label horizontally
            this.div.style.top = (position.y - 110) + 'px'; // Position much higher above the top of grid to avoid overlap
          }
        }

        onRemove() {
          if (this.div) {
            this.div.parentNode.removeChild(this.div);
            this.div = null;
          }
        }

        updatePosition(centerPosition, gridPoints) {
          // Recalculate bounds with new positions
          let bounds = new window.google.maps.LatLngBounds();
          bounds.extend(centerPosition);
          gridPoints.forEach(point => {
            bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
          });
          
          // Update position to top-center of grid
          this.position = new window.google.maps.LatLng(
            bounds.getNorthEast().lat(),
            bounds.getCenter().lng()
          );
          this.draw();
        }
      }

      // Create and store the label
      this.businessMarkerLabel = new MarkerLabel(
        new window.google.maps.LatLng(topCenter.lat, topCenter.lng),
        labelContent,
        this.map,
        this.gridPoints,
        this.businessMarker.getPosition()
      );
    },

    updateDragLabel() {
      if (this.businessMarkerLabel && this.businessMarker) {
        this.businessMarkerLabel.updatePosition(this.businessMarker.getPosition(), this.gridPoints);
      }
    },

    async loadEditData(scanId) {
      try {
        // Show loading indicator
        this.loading = true;
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/duplicate-data/${scanId}`,
          {
            headers: {
              'Authorization': 'Bearer ' + (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])
            }
          }
        );

        if (response.data.success && response.data.data) {
          const editData = response.data.data;
          
          
          // Set search provider based on business source - CRITICAL for ValueSerp
          // If ValueSerp is disabled globally, always use google_places
          if (this.useValueSerp && editData.business.isValueSerp) {
            this.searchProvider = 'value_serp';
            this.businessSearch = editData.business.name;
          } else {
            this.searchProvider = 'google_places';
            this.businessSearch = editData.business.name;
          }
          
          // Set selected business with ALL data - merge raw_data first, then override with specific fields
          this.selectedBusiness = {
            // Start with ALL raw data to ensure nothing is lost
            ...(editData.business.raw_data || {}),
            
            // Then override with structured fields to ensure consistency
            name: editData.business.name,
            title: editData.business.title,
            address: editData.business.address,
            formatted_address: editData.business.formatted_address || editData.business.address,

            // Location fields
            country: editData.business.country || 'United States',
            city: editData.business.city || null,
            state: editData.business.state || null,
            postal_code: editData.business.postal_code || null,
            
            // IDs - critical for ValueSerp
            placeId: editData.business.placeId,
            place_id: editData.business.place_id,
            data_cid: editData.business.data_cid,
            knowledge_graph_id: editData.business.knowledge_graph_id,
            
            // Location
            location: editData.business.location,
            latitude: editData.business.latitude || editData.business.location.lat,
            longitude: editData.business.longitude || editData.business.location.lng,
            
            // Contact & Rating
            phone: editData.business.phone,
            website: editData.business.website,
            link: editData.business.link,
            domain: editData.business.domain,
            rating: editData.business.rating,
            user_ratings_total: editData.business.user_ratings_total,
            reviews: editData.business.reviews,
            rating_breakdown: editData.business.rating_breakdown,
            
            // Categories and Types
            types: editData.business.types || [],
            categories: editData.business.categories || [],
            type: editData.business.type,
            category: editData.business.category,
            primary_category: editData.business.primary_category,
            topics: editData.business.topics,
            
            // Hours
            hours: editData.business.hours,
            opening_hours: editData.business.opening_hours,
            open_state: editData.business.open_state,
            operating_hours: editData.business.operating_hours,
            
            // ValueSerp specific fields
            description: editData.business.description,
            price: editData.business.price,
            thumbnail: editData.business.thumbnail,
            images: editData.business.images || [],
            service_options: editData.business.service_options,
            address_breakdown: editData.business.address_breakdown,
            wheelchair_accessibility: editData.business.wheelchair_accessibility,
            business_availability_modes: editData.business.business_availability_modes,
            
            // Preserve raw ValueSerp data
            raw_valueserp_data: editData.business.raw_valueserp_data,
            full_details: editData.business.full_details,
            
            // Source identification - CRITICAL
            isValueSerp: editData.business.isValueSerp,
            source: editData.business.source
          };
          
          // Set grid center
          this.gridCenter = editData.business.location;
          
          // Set keywords
          this.keywords = editData.keywords || [];
          
          // Set grid configuration
          this.gridSize = editData.gridConfig.gridSize;
          this.radiusValue = editData.gridConfig.radius;
          this.useKilometers = editData.gridConfig.useKilometers;
          this.gridType = editData.gridConfig.gridType;
          
          // Set scan configuration - NO (Copy) suffix for edit mode
          this.scanType = editData.scanConfig.scanType;
          this.gridName = editData.scanConfig.gridName || '';
          this.scanFrequency = editData.scanConfig.scanFrequency || 'daily';
          
          // Set scan time - ensure it's a valid option
          const validScanTimes = ['morning', 'afternoon', 'evening', 'night'];
          const scanTimeFromData = editData.scanConfig.scanTime || 'morning';
          this.scanTime = validScanTimes.includes(scanTimeFromData) ? scanTimeFromData : 'morning';
          
          
          // Force Vue to update the reactive properties
          this.$forceUpdate();
          
          // Wait for map to be ready then set up the business marker and grid
          await this.$nextTick();
          
          // Center map on business
          if (this.map) {
            this.map.setCenter(this.gridCenter);
            this.map.setZoom(13);
            
            // Add business marker
            if (this.businessMarker) {
              this.businessMarker.setMap(null);
            }
            
            this.businessMarker = new window.google.maps.Marker({
              position: this.gridCenter,
              map: this.map,
              title: this.selectedBusiness.name,
              icon: this.getDraggableMarkerIcon(),
              draggable: true,
              animation: window.google.maps.Animation.DROP,
              zIndex: 1000
            });
            
            // Add drag listeners
            this.businessMarker.addListener('drag', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.updateGridWhileDragging();
              this.updateDragLabel();
            });
            
            this.businessMarker.addListener('dragend', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.selectedBusiness.location = this.gridCenter;
              this.drawGrid();
            });
            
            // Draw the grid
            this.drawGrid();
            
            // Add drag label
            this.createDragLabel();
          }
          
          // Show info notification for edit mode
          this.$swal({
            icon: 'info',
            title: 'Edit Mode',
            text: 'You are editing an existing scan. Changes will be saved when you click UPDATE SCAN.',
            timer: 3000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error loading edit data:', error);
        this.$swal({
          icon: 'error',
          title: 'Failed to Load Scan',
          text: 'Could not load the scan data for editing. Please try again.'
        });
        // Navigate back to scans page on error
        setTimeout(() => {
          window.close();
        }, 2000);
      } finally {
        this.loading = false;
      }
    },

    async loadDuplicateData(scanId) {
      try {
        // Show loading indicator
        this.loading = true;
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/duplicate-data/${scanId}`,
          {
            headers: {
              'Authorization': 'Bearer ' + (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])
            }
          }
        );

        if (response.data.success && response.data.data) {
          const duplicateData = response.data.data;
          
          
          // Set search provider based on business source - CRITICAL for ValueSerp
          // If ValueSerp is disabled globally, always use google_places
          if (this.useValueSerp && duplicateData.business.isValueSerp) {
            this.searchProvider = 'value_serp';
            this.businessSearch = duplicateData.business.name;
          } else {
            this.searchProvider = 'google_places';
            this.businessSearch = duplicateData.business.name;
          }
          
          // Set selected business with ALL data - merge raw_data first, then override with specific fields
          // This ensures we don't lose any fields from the original business
          this.selectedBusiness = {
            // Start with ALL raw data to ensure nothing is lost
            ...(duplicateData.business.raw_data || {}),
            
            // Then override with structured fields to ensure consistency
            name: duplicateData.business.name,
            title: duplicateData.business.title,
            address: duplicateData.business.address,
            formatted_address: duplicateData.business.formatted_address || duplicateData.business.address,

            // Location fields
            country: duplicateData.business.country || 'United States',
            city: duplicateData.business.city || null,
            state: duplicateData.business.state || null,
            postal_code: duplicateData.business.postal_code || null,
            
            // IDs - critical for ValueSerp
            placeId: duplicateData.business.placeId,
            place_id: duplicateData.business.place_id,
            data_cid: duplicateData.business.data_cid,
            knowledge_graph_id: duplicateData.business.knowledge_graph_id,
            
            // Location
            location: duplicateData.business.location,
            latitude: duplicateData.business.latitude || duplicateData.business.location.lat,
            longitude: duplicateData.business.longitude || duplicateData.business.location.lng,
            
            // Contact & Rating
            phone: duplicateData.business.phone,
            website: duplicateData.business.website,
            link: duplicateData.business.link,
            domain: duplicateData.business.domain,
            rating: duplicateData.business.rating,
            user_ratings_total: duplicateData.business.user_ratings_total,
            reviews: duplicateData.business.reviews,
            rating_breakdown: duplicateData.business.rating_breakdown,
            
            // Categories and Types
            types: duplicateData.business.types || [],
            categories: duplicateData.business.categories || [],
            type: duplicateData.business.type,
            category: duplicateData.business.category,
            primary_category: duplicateData.business.primary_category,
            topics: duplicateData.business.topics,
            
            // Hours
            hours: duplicateData.business.hours,
            opening_hours: duplicateData.business.opening_hours,
            open_state: duplicateData.business.open_state,
            operating_hours: duplicateData.business.operating_hours,
            
            // ValueSerp specific fields - IMPORTANT
            description: duplicateData.business.description,
            price: duplicateData.business.price,
            thumbnail: duplicateData.business.thumbnail,
            images: duplicateData.business.images || [],
            service_options: duplicateData.business.service_options,
            address_breakdown: duplicateData.business.address_breakdown,
            wheelchair_accessibility: duplicateData.business.wheelchair_accessibility,
            business_availability_modes: duplicateData.business.business_availability_modes,
            
            // Preserve raw ValueSerp data
            raw_valueserp_data: duplicateData.business.raw_valueserp_data,
            full_details: duplicateData.business.full_details,
            
            // Source identification - CRITICAL
            isValueSerp: duplicateData.business.isValueSerp,
            source: duplicateData.business.source
          };
          
          // Set grid center
          this.gridCenter = duplicateData.business.location;
          
          // Set keywords
          this.keywords = duplicateData.keywords || [];
          
          // Set grid configuration
          this.gridSize = duplicateData.gridConfig.gridSize;
          this.radiusValue = duplicateData.gridConfig.radius;
          this.useKilometers = duplicateData.gridConfig.useKilometers;
          this.gridType = duplicateData.gridConfig.gridType;
          
          // Set scan configuration
          this.scanType = duplicateData.scanConfig.scanType;
          this.gridName = duplicateData.scanConfig.gridName ? 
            `${duplicateData.scanConfig.gridName} (Copy)` : '';
          this.scanFrequency = duplicateData.scanConfig.scanFrequency || 'daily';
          
          // Set scan time - ensure it's a valid option
          const validScanTimes = ['morning', 'afternoon', 'evening', 'night'];
          const scanTimeFromData = duplicateData.scanConfig.scanTime || 'morning';
          this.scanTime = validScanTimes.includes(scanTimeFromData) ? scanTimeFromData : 'morning';
          
          
          // Force Vue to update the reactive properties
          this.$forceUpdate();
          
          // Wait for map to be ready then set up the business marker and grid
          await this.$nextTick();
          
          // Center map on business
          if (this.map) {
            this.map.setCenter(this.gridCenter);
            this.map.setZoom(13);
            
            // Add business marker
            if (this.businessMarker) {
              this.businessMarker.setMap(null);
            }
            
            this.businessMarker = new window.google.maps.Marker({
              position: this.gridCenter,
              map: this.map,
              title: this.selectedBusiness.name,
              icon: this.getDraggableMarkerIcon(),
              draggable: true,
              animation: window.google.maps.Animation.DROP,
              zIndex: 1000
            });
            
            // Add drag listeners
            this.businessMarker.addListener('drag', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.updateGridWhileDragging();
              this.updateDragLabel();
            });
            
            this.businessMarker.addListener('dragend', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.selectedBusiness.location = this.gridCenter;
              this.drawGrid();
            });
            
            // Draw the grid
            this.drawGrid();
            
            // Add drag label
            this.createDragLabel();
          }
          
          // Show success notification
          this.$swal({
            icon: 'success',
            title: 'Scan Duplicated',
            text: 'The scan configuration has been loaded. You can modify it if needed before starting.',
            timer: 3000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error loading duplicate data:', error);
        this.$swal({
          icon: 'error',
          title: 'Failed to Load Scan',
          text: 'Could not load the scan data for duplication. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    },

    toggleDistanceUnit() {
      // No need to convert the radius value - just redraw the grid
      // The grid will be drawn with the correct scale based on useKilometers flag
      if (this.selectedBusiness) {
        this.drawGrid();
      }
    },

    updateRadius() {
      if (this.selectedBusiness) {
        this.drawGrid();
      }
    },

    drawGrid() {
      this.clearGrid();

      const center = this.gridCenter;
      const distanceBetweenPins = parseFloat(this.radius);
      // Convert distance between pins to meters based on the current unit
      const stepInMeters = this.useKilometers ? distanceBetweenPins * 1000 : distanceBetweenPins * 1609.34;

      // Get grid dimensions
      const gridSizeParts = this.gridSize.split('x');
      const gridDimension = parseInt(gridSizeParts[0]);

      // Calculate total radius from center to edge (half the grid span)
      // For a 5x5 grid with 5 mile steps: totalRadius = 5 * (5-1)/2 = 5 * 2 = 10 miles from center
      const totalRadiusInMeters = stepInMeters * (gridDimension - 1) / 2;

      if (this.gridType === 'circle') {
        // Draw circle - radius is distance from center to outermost ring
        this.gridCircle = new window.google.maps.Circle({
          center: center,
          radius: totalRadiusInMeters,
          fillColor: '#2477f6',
          fillOpacity: 0.1,
          strokeColor: '#2477f6',
          strokeOpacity: 0.4,
          strokeWeight: 2,
          map: this.map
        });

        this.generateCircularGridPoints();
      } else {
        // Draw square - bounds based on total span
        const bounds = this.calculateSquareBounds(center, totalRadiusInMeters);

        this.gridRectangle = new window.google.maps.Rectangle({
          bounds: bounds,
          fillColor: '#2477f6',
          fillOpacity: 0.1,
          strokeColor: '#2477f6',
          strokeOpacity: 0.4,
          strokeWeight: 2,
          map: this.map
        });

        this.generateSquareGridPoints();
      }

      this.addGridMarkers();

      // Update business marker position if it has been dragged
      if (this.businessMarker) {
        this.businessMarker.setPosition(this.gridCenter);
      }

      // Recreate the drag label after grid is redrawn
      this.createDragLabel();

      // Auto-adjust zoom to fit all markers
      this.fitMapToRadius();
    },
    
    fitMapToRadius() {
      const center = this.gridCenter;
      const distanceBetweenPins = parseFloat(this.radius);
      // Convert distance between pins to meters based on the current unit
      const stepInMeters = this.useKilometers ? distanceBetweenPins * 1000 : distanceBetweenPins * 1609.34;

      // Get grid dimensions
      const gridSizeParts = this.gridSize.split('x');
      const gridDimension = parseInt(gridSizeParts[0]);

      // Calculate total radius from center to edge
      const totalRadiusInMeters = stepInMeters * (gridDimension - 1) / 2;

      // Create bounds that include the entire grid area
      const bounds = new window.google.maps.LatLngBounds();

      // Calculate the bounds based on total radius with 20% padding
      const latChange = (totalRadiusInMeters * 1.2) / 111320;
      const lngChange = (totalRadiusInMeters * 1.2) / (111320 * Math.cos(center.lat * Math.PI / 180));

      bounds.extend(new window.google.maps.LatLng(center.lat - latChange, center.lng - lngChange));
      bounds.extend(new window.google.maps.LatLng(center.lat + latChange, center.lng + lngChange));

      // Fit map to bounds
      this.map.fitBounds(bounds);

      // Optionally set a maximum zoom level to prevent too much zoom
      window.google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 16) {
          this.map.setZoom(16);
        }
      });
    },

    calculateSquareBounds(center, radiusInMeters) {
      const latChange = (radiusInMeters / 111320);
      const lngChange = (radiusInMeters / (111320 * Math.cos(center.lat * Math.PI / 180)));
      
      return {
        north: center.lat + latChange,
        south: center.lat - latChange,
        east: center.lng + lngChange,
        west: center.lng - lngChange
      };
    },

    generateSquareGridPoints() {
      this.gridPoints = [];
      const gridSizeParts = this.gridSize.split('x');
      const rows = parseInt(gridSizeParts[0]);
      const cols = parseInt(gridSizeParts[1]);

      const center = this.gridCenter;
      const distanceBetweenPins = parseFloat(this.radius);
      // Convert distance between pins to meters based on the current unit
      const stepInMeters = this.useKilometers ? distanceBetweenPins * 1000 : distanceBetweenPins * 1609.34;

      // Calculate the step in lat/lng degrees
      const latStep = stepInMeters / 111320;
      const lngStep = stepInMeters / (111320 * Math.cos(center.lat * Math.PI / 180));

      // Calculate the total span from center to edge (half the total grid size)
      const halfRows = (rows - 1) / 2;
      const halfCols = (cols - 1) / 2;

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          // Calculate position relative to center
          const lat = center.lat + (i - halfRows) * latStep;
          const lng = center.lng + (j - halfCols) * lngStep;

          // Add all points including center (center will be saved but not displayed as a blue marker)
          this.gridPoints.push({ lat, lng });
        }
      }
    },

    generateCircularGridPoints() {
      this.gridPoints = [];
      const gridSizeParts = this.gridSize.split('x');
      const gridDimension = parseInt(gridSizeParts[0]);
      const totalPoints = gridDimension * gridDimension; // Total points we need to generate

      const center = this.gridCenter;
      const distanceBetweenPins = parseFloat(this.radius);
      // Convert distance between pins to meters based on the current unit
      const stepInMeters = this.useKilometers ? distanceBetweenPins * 1000 : distanceBetweenPins * 1609.34;

      // Calculate total radius from center to outermost ring
      // For a 5x5 grid, maxRings = 2, so outermost ring is at 2 * stepInMeters from center
      const maxRings = Math.ceil((gridDimension - 1) / 2);

      // Add center point first
      this.gridPoints.push({ lat: center.lat, lng: center.lng });

      let pointsAdded = 1; // We've already added the center point

      // Distribute remaining points in concentric circles
      for (let ring = 1; ring <= maxRings && pointsAdded < totalPoints; ring++) {
        // Each ring is exactly stepInMeters apart from the previous ring
        const ringRadius = stepInMeters * ring;

        // Calculate how many points to place in this ring
        // We want to distribute points somewhat evenly
        let pointsInRing;
        if (ring === maxRings) {
          // Last ring gets all remaining points
          pointsInRing = totalPoints - pointsAdded;
        } else {
          // Distribute points proportionally to ring circumference
          pointsInRing = Math.min(ring * 8, totalPoints - pointsAdded);
        }

        for (let i = 0; i < pointsInRing; i++) {
          const angle = (2 * Math.PI * i) / pointsInRing;
          const lat = center.lat + (ringRadius / 111320) * Math.sin(angle);
          const lng = center.lng + (ringRadius / (111320 * Math.cos(center.lat * Math.PI / 180))) * Math.cos(angle);
          this.gridPoints.push({ lat, lng });
        }

        pointsAdded += pointsInRing;
      }
    },

    addGridMarkers() {
      this.gridMarkers.forEach(marker => marker.setMap(null));
      this.gridMarkers = [];

      // Add grid point markers with custom blue pin design
      // Skip the first point (center) to avoid overlapping with the business marker
      this.gridPoints.forEach((point, index) => {
        // Check if this is the center point
        const isCenter = Math.abs(point.lat - this.gridCenter.lat) < 0.00001 &&
                        Math.abs(point.lng - this.gridCenter.lng) < 0.00001;

        // Only add marker if it's not the center point
        if (!isCenter) {
          const marker = new window.google.maps.Marker({
            position: point,
            map: this.map,
            icon: this.getCustomMarkerIcon(),
            title: `Grid Point ${index + 1}`,
            cursor: 'pointer'
          });

          // Add mouseover event to show white cross
          marker.addListener('mouseover', () => {
            if (!isCenter) {
              this.hoveredMarkerIndex = index;
              const isDisabled = this.disabledMarkers.includes(index);
              marker.setIcon(this.getMarkerIconWithCross(isDisabled));
            }
          });

          // Add mouseout event to remove white cross
          marker.addListener('mouseout', () => {
            this.hoveredMarkerIndex = null;
            const isDisabled = this.disabledMarkers.includes(index);
            marker.setIcon(isDisabled ? this.getDisabledMarkerIcon() : this.getCustomMarkerIcon());
          });

          // Add click event to disable/enable marker
          marker.addListener('click', () => {
            if (!isCenter) {
              this.toggleMarkerDisabled(index, marker);
            }
          });

          // Store marker with its index
          marker.gridPointIndex = index;
          this.gridMarkers.push(marker);
        }
      });
    },

    updateGridWhileDragging() {
      // Clear existing grid shapes and markers
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null);
        this.gridRectangle = null;
      }
      if (this.gridCircle) {
        this.gridCircle.setMap(null);
        this.gridCircle = null;
      }

      // Clear grid markers
      this.gridMarkers.forEach(marker => marker.setMap(null));
      this.gridMarkers = [];

      const center = this.gridCenter;
      const distanceBetweenPins = parseFloat(this.radius);
      // Convert distance between pins to meters based on the current unit
      const stepInMeters = this.useKilometers ? distanceBetweenPins * 1000 : distanceBetweenPins * 1609.34;

      // Get grid dimensions
      const gridSizeParts = this.gridSize.split('x');
      const gridDimension = parseInt(gridSizeParts[0]);

      // Calculate total radius from center to edge
      const totalRadiusInMeters = stepInMeters * (gridDimension - 1) / 2;

      // Redraw grid shape and regenerate points based on type
      if (this.gridType === 'circle') {
        this.gridCircle = new window.google.maps.Circle({
          center: center,
          radius: totalRadiusInMeters,
          fillColor: '#2477f6',
          fillOpacity: 0.1,
          strokeColor: '#2477f6',
          strokeOpacity: 0.4,
          strokeWeight: 2,
          map: this.map
        });
        this.generateCircularGridPoints();
      } else {
        const bounds = this.calculateSquareBounds(center, totalRadiusInMeters);
        this.gridRectangle = new window.google.maps.Rectangle({
          bounds: bounds,
          fillColor: '#2477f6',
          fillOpacity: 0.1,
          strokeColor: '#2477f6',
          strokeOpacity: 0.4,
          strokeWeight: 2,
          map: this.map
        });
        this.generateSquareGridPoints();
      }

      // Re-add grid markers
      this.addGridMarkers();
    },
    
    clearGrid() {
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null);
        this.gridRectangle = null;
      }
      if (this.gridCircle) {
        this.gridCircle.setMap(null);
        this.gridCircle = null;
      }
      if (this.businessMarkerLabel) {
        this.businessMarkerLabel.setMap(null);
        this.businessMarkerLabel = null;
      }
      this.gridMarkers.forEach(marker => marker.setMap(null));
      this.gridMarkers = [];
      this.gridPoints = [];
      this.disabledMarkers = []; // Clear disabled markers when grid is cleared
    },

    setGridType(type) {
      this.gridType = type;
      if (this.selectedBusiness) {
        this.drawGrid();
      }
    },

    addKeyword() {
      if (this.keywordInput.trim() && !this.keywords.includes(this.keywordInput.trim())) {
        this.keywords.push(this.keywordInput.trim());
        this.keywordInput = '';
      }
    },

    removeKeyword(index) {
      this.keywords.splice(index, 1);
    },

    toggleMarkerDisabled(index, marker) {
      const disabledIndex = this.disabledMarkers.indexOf(index);
      if (disabledIndex > -1) {
        // Re-enable the marker
        this.disabledMarkers.splice(disabledIndex, 1);
        marker.setIcon(this.hoveredMarkerIndex === index ? this.getMarkerIconWithCross(false) : this.getCustomMarkerIcon());
      } else {
        // Disable the marker
        this.disabledMarkers.push(index);
        marker.setIcon(this.getDisabledMarkerIcon());
      }
    },

    enableAllMarkers() {
      // Clear all disabled markers
      this.disabledMarkers = [];

      // Reset all marker icons
      this.gridMarkers.forEach((marker) => {
        if (marker.gridPointIndex !== undefined) {
          marker.setIcon(this.getCustomMarkerIcon());
        }
      });
    },

    async fetchKeywordSuggestions() {
      if (!this.selectedBusiness) return;

      this.loadingSuggestions = true;
      this.suggestedKeywords = [];
      this.showAllSuggestions = false; // Reset to show only first 2

      try {
        // Extract business details
        const businessName = this.selectedBusiness.name || this.selectedBusiness.title || '';
        const city = this.selectedBusiness.city || this.extractCityFromBusiness();
        const state = this.selectedBusiness.state || this.extractStateFromBusiness();

        // Get primary category from types array or use first type
        let primaryCategory = '';
        if (this.selectedBusiness.types && this.selectedBusiness.types.length > 0) {
          primaryCategory = this.selectedBusiness.types[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (this.selectedBusiness.primary_category) {
          primaryCategory = this.selectedBusiness.primary_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (this.selectedBusiness.category) {
          primaryCategory = this.selectedBusiness.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (this.selectedBusiness.type) {
          primaryCategory = this.selectedBusiness.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else {
          // Fallback to generic business category
          primaryCategory = 'Local Business';
        }

        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/keyword-suggestions`,
          {
            businessName,
            primaryCategory,
            city,
            state
          },
          {
            headers: {
              'Authorization': 'Bearer ' + (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])
            }
          }
        );

        if (response.data.success && response.data.keywords) {
          // Filter out keywords that are already in the keywords list
          this.suggestedKeywords = response.data.keywords.filter(
            keyword => !this.keywords.includes(keyword)
          );
        }
      } catch (error) {
        console.error('Failed to fetch keyword suggestions:', error);
        console.error('Error details:', error.response ? error.response.data : error.message);
      } finally {
        this.loadingSuggestions = false;
      }
    },

    addSuggestedKeyword(keyword) {
      if (!this.keywords.includes(keyword)) {
        this.keywords.push(keyword);
        // Remove from suggested keywords
        const index = this.suggestedKeywords.indexOf(keyword);
        if (index > -1) {
          this.suggestedKeywords.splice(index, 1);
        }
      }
    },

    toggleShowAllSuggestions() {
      this.showAllSuggestions = !this.showAllSuggestions;
    },

    extractCityFromBusiness() {
      // Try to extract city from address
      if (this.selectedBusiness.formatted_address || this.selectedBusiness.address) {
        const address = this.selectedBusiness.formatted_address || this.selectedBusiness.address;
        const parts = address.split(',');
        if (parts.length >= 2) {
          // Try to get the second-to-last part (usually city)
          const cityPart = parts[parts.length - 2].trim();
          // Remove any state abbreviation or zip code that might be in the same part
          return cityPart.replace(/\s+[A-Z]{2}\s+\d{5}.*$/, '').trim();
        }
      }
      // Fallback to a generic city if extraction fails
      return 'Local Area';
    },

    extractStateFromBusiness() {
      // Try to extract state from address
      if (this.selectedBusiness.formatted_address || this.selectedBusiness.address) {
        const address = this.selectedBusiness.formatted_address || this.selectedBusiness.address;
        const parts = address.split(',');
        if (parts.length >= 2) {
          const stateZip = parts[parts.length - 2].trim();
          const statePart = stateZip.split(' ')[0];
          return statePart;
        }
      }
      return '';
    },

    onScanTypeChange() {
      if (this.scanType === 'one-time') {
        this.gridName = '';
        this.scanFrequency = 'daily';
        this.scanTime = 'morning';
      }
    },

    async startScan() {
      if (!this.selectedBusiness) {
        this.$swal({
          icon: 'warning',
          title: 'Business Required',
          text: 'Please search and select a business.'
        });
        return;
      }

      if (this.keywords.length === 0) {
        this.$swal({
          icon: 'warning',
          title: 'Keywords Required',
          text: 'Please add at least one keyword.'
        });
        return;
      }
    
      if (this.scanType === 'recurring' && !this.gridName) {
        this.$swal({
          icon: 'warning',
          title: 'Grid Name Required',
          text: 'Please enter a grid name for recurring scans.'
        });
        return;
      }

      this.loading = true;

      try {
        if (this.scanType === 'one-time') {
          await this.executeQuickScan();
        } else {
          await this.executeGridScan();
        }
      } catch (error) {
        console.error('Error starting scan:', error);

        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to start scan. Please try again.';
        const errorDetails = error.response && error.response.data && error.response.data.details;
        const isInsufficientCredits = error.response && error.response.status === 403;

        // If in embed mode, send postMessage to parent
        if (this.isEmbedMode) {
          const allowedOrigins = [
            'https://keywords.bitsmiths.dev',
            'https://app.seomaven.ai',
            'http://localhost:3000'
          ];

          // Send message to all allowed origins
          allowedOrigins.forEach(origin => {
            try {
              window.parent.postMessage({
                type: 'SCAN_CREATION_ERROR',
                error: {
                  message: errorMessage,
                  details: errorDetails,
                  isInsufficientCredits: isInsufficientCredits,
                  statusCode: error.response ? error.response.status : null
                }
              }, origin);
            } catch (e) {
              console.error('Error sending postMessage to origin:', origin, e);
            }
          });

          // DON'T show local popup in embed mode - let parent handle it
          // console.log('[CreateScan] Error sent to parent via postMessage, parent will show modal');
        } else {
          // Normal mode - show popup
          this.$swal({
            icon: 'error',
            title: isInsufficientCredits ? 'Insufficient Credits' : 'Error',
            text: errorMessage
          });
        }

        this.loading = false;
      }
    },

    async executeQuickScan() {
      // Quick scans cannot be edited - they are one-time only
      // If in edit mode for a quick scan, we should create a new one
      if (this.isEditMode) {
        this.$swal({
          icon: 'info',
          title: 'Quick Scan Note',
          text: 'Quick scans are one-time scans and cannot be updated. A new scan will be created with your changes.',
          confirmButtonText: 'OK, Create New Scan'
        });
        // Reset edit mode for quick scan
        this.isEditMode = false;
        this.editingGridId = null;
      }

      // Force google_places if Value SERP is disabled globally
      if (!this.useValueSerp && this.searchProvider === 'value_serp') {
        this.searchProvider = 'google_places';
      }

      // Validate API keys based on search provider
      if (this.searchProvider === 'value_serp') {
        // For ValueSerp searches, only check ValueSerp API key
        await this.$store.dispatch('apiKeys/fetchValueSerpKey');
        const valueSerpStatus = this.$store.getters['apiKeys/valueSerpStatus'];

        if (!valueSerpStatus || valueSerpStatus !== 1) {
          this.loading = false;
          // Check if in embed mode
          if (this.$store.getters['embed/isEmbedMode']) {
            // In embed mode, show dialog with option to go to parent website settings
            this.$swal({
              icon: 'info',
              title: 'API Key Not Configured',
              text: 'Value SERP API key is not configured. Please go to the settings page to integrate the API.',
              confirmButtonText: 'Go to Settings Page',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(() => {
              // Open parent website's settings page in a new tab
              window.open('/settings', '_blank');
              this.$router.push('/scans');
            });
          } else {
            // Normal mode - show full settings option
            this.$swal({
              icon: 'error',
              title: 'Value SERP API Key Required',
              text: 'A valid Value SERP API key is required for Value SERP searches. Please configure it in Settings → Integration.',
              confirmButtonText: 'Go to Settings',
              showCancelButton: true,
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                this.$router.push('/user-settings');
              }
            });
          }
          return;
        }
      } else if (this.searchProvider === 'google_places') {
        // For Google Places searches, check DataForSeo credentials directly from Supabase
        const { supabase } = await import('@/config/supabase');
        let hasDataForSeoCredentials = false;

        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (session && session.user) {
            // Check user metadata first (fastest)
            const userMetadata = session.user.user_metadata || {};
            if (userMetadata.dataForSeoUsername && userMetadata.dataForSeoPassword) {
              hasDataForSeoCredentials = true;
            } else {
              // Check user_profiles table
              const { data: userProfile } = await supabase
                .from('user_profiles')
                .select('data_for_seo_username, data_for_seo_password')
                .eq('id', session.user.id)
                .single();

              if (userProfile && userProfile.data_for_seo_username && userProfile.data_for_seo_password) {
                hasDataForSeoCredentials = true;
              }
            }
          }

          // If no Supabase session, fall back to store check
          if (!session) {
            await this.$store.dispatch('apiKeys/fetchDataForSeoKey');
            const dataForSeoStatus = this.$store.getters['apiKeys/dataForSeoStatus'];
            hasDataForSeoCredentials = dataForSeoStatus === 1;
          }
        } catch (error) {
          console.error('Error checking DataForSeo credentials:', error);
          // Fall back to store check
          await this.$store.dispatch('apiKeys/fetchDataForSeoKey');
          const dataForSeoStatus = this.$store.getters['apiKeys/dataForSeoStatus'];
          hasDataForSeoCredentials = dataForSeoStatus === 1;
        }

        if (!hasDataForSeoCredentials) {
          this.loading = false;
          // Check if in embed mode
          if (this.$store.getters['embed/isEmbedMode']) {
            // In embed mode, show dialog with option to go to parent website settings
            this.$swal({
              icon: 'info',
              title: 'Credentials Not Configured',
              text: 'DataForSeo credentials are not configured. Please go to the settings page to integrate the API.',
              confirmButtonText: 'Go to Settings Page',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(() => {
              // Open parent website's settings page in a new tab
              window.open('/settings', '_blank');
              this.$router.push('/scans');
            });
          } else {
            // Normal mode - show full settings option
            this.$swal({
              icon: 'error',
              title: 'DataForSeo Credentials Required',
              text: 'Valid DataForSeo credentials are required for Google Places searches. Please configure them in Settings → Integration.',
              confirmButtonText: 'Go to Settings',
              showCancelButton: true,
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                this.$router.push('/user-settings');
              }
            });
          }
          return;
        }
      }

      // Create an array to store all search IDs
      const searchIds = [];
      const totalKeywords = this.keywords.length;

      // Create a separate search for each keyword
      for (let i = 0; i < this.keywords.length; i++) {
        const keyword = this.keywords[i];

        // Filter out disabled grid points
        const enabledGridPoints = this.gridPoints.filter((point, index) => {
          // Always include the center point
          const isCenter = Math.abs(point.lat - this.gridCenter.lat) < 0.00001 &&
                          Math.abs(point.lng - this.gridCenter.lng) < 0.00001;
          return isCenter || !this.disabledMarkers.includes(index);
        });

        const scanData = {
          search_type: 'quick_scan',
          business_name: this.selectedBusiness.name,
          place_id: this.selectedBusiness.placeId,
          business: this.selectedBusiness,
          keywords: [keyword], // Send only one keyword per search
          total_keywords: totalKeywords, // Send total keyword count for credit validation
          grid_size: this.gridSize,
          radius: this.radius,
          distance_type: this.useKilometers ? 'kilometers' : 'miles',
          grid_shape: this.gridType || 'square',
          address: this.selectedBusiness.address,
          latitude: this.gridCenter.lat,
          longitude: this.gridCenter.lng,
          grid_points: enabledGridPoints,
          search_provider: this.searchProvider
        };

        try {
          const response = await axios.post(
            `${process.env.VUE_APP_API_ENDPOINT}/api/scans`,
            scanData,
            {
              headers: {
                Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
                "Content-Type": "application/json"
              }
            }
          );
          
          searchIds.push(response.data.search_id);
          
          // Update progress if multiple keywords
          if (totalKeywords > 1) {
            const progress = Math.round(((i + 1) / totalKeywords) * 100);
          }
        } catch (error) {
          console.error(`Error creating search for keyword "${keyword}":`, error);
          
          // If any search fails, show error but continue with others
          if (i === 0) {
            // If first search fails, throw error
            throw error;
          }
          // Otherwise, log but continue
        }
      }
      
      // Store the last search ID (or first if you prefer)
      this.lastSearchId = searchIds[searchIds.length - 1] || searchIds[0];
      this.loading = false;

      // Show success modal with information about multiple searches if applicable
      if (searchIds.length > 0) {
        this.showSuccessModal = true;

        if (totalKeywords > 1) {
          // Optionally show that multiple searches were created
        }
      }

      // Track user journey for quick scan creation or duplication
      if (searchIds.length > 0) {
        if (this.isDuplicateMode) {
          // Track as duplicate
          trackUserJourney('gg_scan_duplicated', {
            original_scan_id: this.$route.query.duplicateFrom,
            new_scan_id: this.lastSearchId,
            scan_type: 'quick_scan'
          });
        } else {
          // Track as new scan
          trackUserJourney('gg_quick_scan_added', {
            scan_id: this.lastSearchId,
            search_provider: this.searchProvider,
            keyword: this.keywords.join(', '),
            location: this.selectedBusiness.address,
            business_name: this.selectedBusiness.name,
            grid_size: this.gridSize,
            grid_type: this.gridType
          });
        }
      }

      if (this.$posthog) {
        this.$posthog.capture('quick_scan_created', {
          keyword_count: this.keywords.length,
          searches_created: searchIds.length,
          grid_size: this.gridSize,
          radius: this.radius,
          business_name: this.selectedBusiness.name
        });
      }
    },

    async executeGridScan() {
      const timeMapping = {
        'morning': '09:00',
        'afternoon': '14:00',
        'evening': '18:00',
        'night': '21:00'
      };

      const scanTimeFormatted = this.scanFrequency === 'manual' ? null : timeMapping[this.scanTime];

      const gridScanData = {
        business_name: this.selectedBusiness.name,
        business: this.selectedBusiness,
        address: this.selectedBusiness.address,
        latitude: this.gridCenter.lat,
        longitude: this.gridCenter.lng,
        grid_name: this.gridName,
        keywords: this.keywords,
        grid_size: this.gridSize,
        radius: this.radius,
        distance_type: this.useKilometers ? 'kilometers' : 'miles',
        grid_shape: this.gridType || 'square',
        grid_points: this.gridPoints,
        search_provider: this.searchProvider,
        scan_frequency: this.scanFrequency,
        scan_time: scanTimeFormatted,
        notification_email: '',
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        trigger_immediately: this.scanFrequency === 'manual' && !this.isEditMode // Don't trigger immediately if editing
      };

      let response;
      
      // Determine if this is create or update
      if (this.isEditMode && this.editingGridId) {
        // UPDATE existing scan
        response = await axios.put(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${this.editingGridId}`,
          gridScanData,
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else {
        // CREATE new scan
        response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan`,
          gridScanData,
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
      }

      this.lastSearchId = this.isEditMode ? this.editingGridId : response.data.search_id;
      this.loading = false;

      // Show different success message for edit vs create
      if (this.isEditMode) {
        // Track scan edit
        trackUserJourney('gg_scan_edited', {
          scan_id: this.editingGridId,
          scan_type: 'recurring_scan'
        });

        this.$swal({
          icon: 'success',
          title: 'Scan Updated',
          text: 'Your scan has been successfully updated. The changes will take effect on the next scheduled run.',
          confirmButtonText: 'OK'
        }).then(() => {
          // Close the window after update
          window.close();
        });
      } else {
        this.showSuccessModal = true;

        // Track user journey for recurring scan creation or duplication
        if (this.isDuplicateMode) {
          // Track as duplicate
          trackUserJourney('gg_scan_duplicated', {
            original_scan_id: this.$route.query.duplicateFrom,
            new_scan_id: this.lastSearchId,
            scan_type: 'recurring_scan'
          });
        } else {
          // Track as new scan
          trackUserJourney('gg_recurring_scan_added', {
            scan_id: this.lastSearchId,
            search_provider: this.searchProvider,
            keyword: this.keywords.join(', '),
            location: this.selectedBusiness.address,
            business_name: this.selectedBusiness.name,
            grid_size: this.gridSize,
            grid_type: this.gridType,
            scan_frequency: this.scanFrequency,
            scan_time: this.scanTime
          });
        }

        if (this.$posthog) {
          this.$posthog.capture('grid_scan_created', {
            grid_size: this.gridSize,
            keyword_count: this.keywords.length,
            scan_frequency: this.scanFrequency,
            business_name: this.selectedBusiness.name
          });
        }
      }
    },

    viewHistory() {
      if (this.isEmbedMode) {
        this.goBackToScans();
      } else {
        this.$router.push('/scans');
      }
    },

    startNewScan() {
      // Close the success modal
      this.showSuccessModal = false;
      
      // Reset the form using the same logic as pencil icon
      this.resetBusinessSelection();
    },

    viewReport() {
      if (this.lastSearchId) {
        // For one-time scans, go to view-grid
        // For recurring scans, go to recurring-grid
        if (this.scanType === 'one-time') {
          if (this.isEmbedMode) {
            this.navigateInIframe(`/view-grid/${this.lastSearchId}`);
          } else {
            this.$router.push({ name: 'ViewGrid', params: { id: this.lastSearchId } });
          }
        } else {
          if (this.isEmbedMode) {
            this.navigateInIframe(`/recurring-grid/${this.lastSearchId}`);
          } else {
            this.$router.push({ name: 'RecurringGrid', params: { id: this.lastSearchId } });
          }
        }
      }
    },

    resetBusinessSelection() {
      // Clear business selection
      this.selectedBusiness = null;
      this.businessSearch = '';
      
      // Clear map and markers
      if (this.businessMarker) {
        this.businessMarker.setMap(null);
        this.businessMarker = null;
      }
      
      // Clear grid
      this.clearGrid();
      
      // Reset form
      this.keywords = [];
      this.keywordInput = '';
      this.scanType = 'one-time';
      this.scanTitle = '';
      this.gridName = '';
      this.gridType = 'square';
      this.gridSize = '3x3';
      this.radiusValue = 1;
      this.useKilometers = false;
      
      // Reset map to default view
      if (this.map) {
        this.map.setCenter({ lat: 33.749, lng: -84.388 });
        this.map.setZoom(12);
      }
      
      // Re-enable autocomplete
      this.$nextTick(() => {
        this.initAutocomplete();
      });
      
      // Reset Value SERP fields
      this.searchProvider = 'google_places';
      this.valueSerpSearchResults = [];
      this.showValueSerpDropdown = false;
    },
    
    // Value SERP Methods
    async performValueSerpSearch() {
      if (!this.businessSearch || this.businessSearch.trim().length < 3) {
        // If search is empty or too short, close ValueSERP and re-enable Google
        this.closeValueSerpDropdown();
        this.$swal({
          icon: 'warning',
          title: 'Search Term Required',
          text: 'Please enter at least 3 characters to search',
          timer: 2000
        });
        return;
      }
      
      // Check API key based on configuration
      if (this.useValueSerp) {
        // Check if ValueSerp API key is valid before searching
        await this.$store.dispatch('apiKeys/fetchValueSerpKey');
        const valueSerpStatus = this.$store.getters['apiKeys/valueSerpStatus'];

        if (!valueSerpStatus || valueSerpStatus !== 1) {
          // Check if in embed mode
          if (this.$store.getters['embed/isEmbedMode']) {
            // In embed mode, show dialog with option to go to parent website settings
            this.$swal({
              icon: 'info',
              title: 'API Key Not Configured',
              text: 'Value SERP API key is not configured. Please go to the settings page to integrate the API.',
              confirmButtonText: 'Go to Settings Page',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(() => {
              // Open parent website's settings page in a new tab
              window.open('/settings', '_blank');
              this.$router.push('/scans');
            });
          } else {
            // Normal mode - show full settings option
            this.$swal({
              icon: 'error',
              title: 'Value SERP API Key Required',
              text: 'A valid Value SERP API key is required to use Value SERP search. Please configure it in Settings → Integration.',
              confirmButtonText: 'Go to Settings',
              showCancelButton: true,
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                this.$router.push('/user-settings');
              }
            });
          }
          return;
        }
      } else {
        // Check DataForSeo credentials directly from Supabase
        const { supabase } = await import('@/config/supabase');
        let hasDataForSeoCredentials = false;

        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (session && session.user) {
            // Check user metadata first (fastest)
            const userMetadata = session.user.user_metadata || {};
            if (userMetadata.dataForSeoUsername && userMetadata.dataForSeoPassword) {
              hasDataForSeoCredentials = true;
            } else {
              // Check user_profiles table
              const { data: userProfile } = await supabase
                .from('user_profiles')
                .select('data_for_seo_username, data_for_seo_password')
                .eq('id', session.user.id)
                .single();

              if (userProfile && userProfile.data_for_seo_username && userProfile.data_for_seo_password) {
                hasDataForSeoCredentials = true;
              }
            }
          }

          // If no Supabase session, fall back to store check
          if (!session) {
            await this.$store.dispatch('apiKeys/fetchDataForSeoKey');
            const dataForSeoStatus = this.$store.getters['apiKeys/dataForSeoStatus'];
            hasDataForSeoCredentials = dataForSeoStatus === 1;
          }
        } catch (error) {
          console.error('Error checking DataForSeo credentials:', error);
          // Fall back to store check
          await this.$store.dispatch('apiKeys/fetchDataForSeoKey');
          const dataForSeoStatus = this.$store.getters['apiKeys/dataForSeoStatus'];
          hasDataForSeoCredentials = dataForSeoStatus === 1;
        }

        if (!hasDataForSeoCredentials) {
          // Check if in embed mode
          if (this.$store.getters['embed/isEmbedMode']) {
            // In embed mode, show simplified dialog and redirect to scans
            this.$swal({
              icon: 'info',
              title: 'Credentials Not Configured',
              text: 'DataForSeo credentials are not configured.',
              confirmButtonText: 'OK',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(() => {
              this.$router.push('/scans');
            });
          } else {
            // Normal mode - show full settings option
            this.$swal({
              icon: 'error',
              title: 'DataForSeo Credentials Required',
              text: 'Valid DataForSeo credentials are required to search for businesses. Please configure them in Settings → Integration.',
              confirmButtonText: 'Go to Settings',
              showCancelButton: true,
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                this.$router.push('/user-settings');
              }
            });
          }
          return;
        }
      }

      // Disable Google autocomplete when ValueSERP search starts
      this.disableGoogleAutocomplete();

      this.valueSerpSearchLoading = true;
      this.showValueSerpDropdown = true;
      this.valueSerpSearchResults = [];

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/search`,
          {
            keyword: this.businessSearch
          },
          {
            headers: {
              'Authorization': 'Bearer ' + (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])
            }
          }
        );
        
        if (response.data.success && response.data.suggestions) {
          this.valueSerpSearchResults = response.data.suggestions;
          
          if (this.valueSerpSearchResults.length === 0) {
            this.$swal({
              icon: 'info',
              title: 'No Results',
              text: 'No businesses found with ValueSERP. Try different search terms.',
              timer: 3000
            });
          }
        } else if (response.data.success && response.data.data) {
          // Fallback for old API response format
          this.valueSerpSearchResults = response.data.data;
        } else {
          this.valueSerpSearchResults = [];
        }
      } catch (error) {
        console.error('ValueSERP search error:', error);
        this.$swal({
          icon: 'error',
          title: 'Search Failed',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to search businesses'
        });
        this.showValueSerpDropdown = false;
      } finally {
        this.valueSerpSearchLoading = false;
      }
    },
    
    closeValueSerpDropdown() {
      this.showValueSerpDropdown = false;
      this.valueSerpSearchResults = [];
      // Re-enable Google autocomplete when closing ValueSERP
      this.enableGoogleAutocomplete();
    },

    isServiceAreaBusiness(result) {
      // Check if this is a Service Area Business (SAB)
      if (!result) return false;

      // First check if backend explicitly marked it as SAB
      // This works for both DataForSEO and ValueSerp
      if (result.is_sab === true) {
        return true;
      }

      // Check for service_area field from API (both providers might have this)
      if (result.service_area) {
        return true;
      }

      // Check if address contains "Service area" or similar indicators
      if (result.address && result.address.toLowerCase().includes('service area')) {
        return true;
      }

      // For ValueSerp results, also do address-based detection
      // ValueSerp doesn't have is_sab flag, so we need to detect based on address pattern
      if (result.source === 'value_serp' || (!result.source && !result.isDataForSeo)) {
        // Check if there's no street address but has city/state
        const hasStreetAddress = result.street ||
          (result.address && result.address.match(/\d+\s+[\w\s]+(?:st|street|ave|avenue|rd|road|blvd|boulevard|ln|lane|dr|drive|way|ct|court|pl|place)/i));
        const hasLocation = result.city || result.state;

        // If has location info but no street address, likely an SAB
        if (!hasStreetAddress && hasLocation) {
          return true;
        }
      }

      // For DataForSEO, the backend should have already set is_sab based on null address
      // So we don't need additional checks here

      return false;
    },
    
    formatValueSerpAddress(result) {
      // Format address to match Google's style
      // For SABs, show available location info (city, state, country)
      if (this.isServiceAreaBusiness(result)) {
        const parts = [];
        if (result.city) parts.push(result.city);
        if (result.state) parts.push(result.state);
        if (result.postal_code) parts.push(result.postal_code);
        // Always show country for SABs
        if (result.country) {
          parts.push(result.country);
        } else {
          // Default to United States if no country specified
          parts.push('United States');
        }
        return parts.length > 0 ? parts.join(', ') : 'Service Area Business';
      }

      // For regular businesses with full address
      if (result.address) {
        return result.address;
      }

      // Build address from components if available
      const parts = [];
      if (result.street) parts.push(result.street);
      if (result.city) parts.push(result.city);
      if (result.state) parts.push(result.state);
      if (result.postal_code) parts.push(result.postal_code);
      // Show country for all businesses
      if (result.country) {
        parts.push(result.country);
      }
      
      // If no address components, try to extract from extensions
      if (parts.length === 0 && result.extensions) {
        // Find the address-like string in extensions
        const addressExtension = result.extensions.find(ext => 
          ext.includes(',') && (ext.includes('India') || ext.includes('USA') || ext.includes('Delhi'))
        );
        if (addressExtension) {
          return addressExtension;
        }
      }
      
      return parts.join(', ') || 'Address not available';
    },
    
    handleInputFocus() {
      // If ValueSERP dropdown is active, don't enable Google autocomplete
      if (this.showValueSerpDropdown || this.valueSerpSearchLoading) {
        if (this.autocomplete) {
          // Temporarily disable Google autocomplete
          this.disableGoogleAutocomplete();
        }
      }
    },
    
    handleInputBlur() {
      // Re-enable Google autocomplete after a delay if ValueSERP is not active
      setTimeout(() => {
        if (!this.showValueSerpDropdown && !this.valueSerpSearchLoading) {
          this.enableGoogleAutocomplete();
        }
      }, 200);
    },
    
    disableGoogleAutocomplete() {
      if (this.autocomplete) {
        // Remove Google autocomplete listeners temporarily
        window.google.maps.event.clearInstanceListeners(this.autocomplete);
        // Hide any open autocomplete dropdown
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach(container => {
          container.style.display = 'none';
          container.style.visibility = 'hidden';
        });
        // Add class to body to ensure Google autocomplete stays hidden
        document.body.classList.add('value-serp-active');
      }
    },
    
    enableGoogleAutocomplete() {
      // Remove the class that hides Google autocomplete
      document.body.classList.remove('value-serp-active');
      
      if (this.autocomplete && !this.showValueSerpDropdown) {
        // Re-initialize autocomplete if needed
        this.initAutocomplete();
      }
    },
    
    
    onValueSerpInput() {
      // Don't search if input is too short
      if (this.valueSerpKeyword.length < 3) {
        this.valueSerpSearchResults = [];
        return;
      }
      
      // Debounce the search
      clearTimeout(this.valueSerpSearchTimeout);
      this.valueSerpSearchTimeout = setTimeout(() => {
        this.searchValueSerp();
      }, 300);
    },
    
    async searchValueSerp() {
      if (!this.valueSerpKeyword.trim() || this.valueSerpKeyword.length < 3) {
        return;
      }
      
      this.valueSerpSearchLoading = true;
      this.showValueSerpDropdown = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/search`,
          { 
            keyword: this.valueSerpKeyword
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.data && response.data.success && response.data.suggestions) {
          this.valueSerpSearchResults = response.data.suggestions.slice(0, 10); // Limit to 10 results
        } else {
          this.valueSerpSearchResults = [];
        }
      } catch (error) {
        console.error('Value SERP search error:', error);
        this.valueSerpSearchResults = [];
      } finally {
        this.valueSerpSearchLoading = false;
      }
    },
    
    async selectValueSerpBusiness(business) {
      // Close dropdown immediately
      this.showValueSerpDropdown = false;
      this.hoveredIndex = -1;
      
      // Show loading state and update input
      this.valueSerpDetailLoading = true;
      this.businessSearch = 'Loading: ' + business.title + '...';
      
      try {
        // Get full place details with coordinates
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/place-details`,
          {
            data_cid: business.data_cid,
            place_id: business.place_id || business.data_id
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.data && response.data.success && response.data.data) {
          const place = response.data.data;
          // Check if full_details exists and use it for complete data
          const fullDetails = place.full_details || place;
          
          // Successfully got the place details - capture ALL fields
          this.selectedBusiness = {
            // Basic info
            name: fullDetails.title || place.title || business.title || '',
            title: fullDetails.title || place.title || business.title || '',
            address: fullDetails.address || place.address || business.address || '',
            formatted_address: fullDetails.address || place.address || business.address || '',
            placeId: place.place_id || business.place_id || null,
            place_id: place.place_id || business.place_id || null,
            data_cid: fullDetails.data_cid || place.data_cid || business.data_cid,
            data_id: fullDetails.data_id || null,

            // Location fields
            country: fullDetails.country || place.country || business.country || 'United States',
            city: fullDetails.city || place.city || business.city || null,
            state: fullDetails.state || place.state || business.state || null,
            postal_code: fullDetails.postal_code || place.postal_code || business.postal_code || null,
            
            // Location
            location: {
              lat: parseFloat(place.latitude || fullDetails.latitude || (fullDetails.gps_coordinates && fullDetails.gps_coordinates.latitude) || 0),
              lng: parseFloat(place.longitude || fullDetails.longitude || (fullDetails.gps_coordinates && fullDetails.gps_coordinates.longitude) || 0)
            },
            latitude: place.latitude || fullDetails.latitude || (fullDetails.gps_coordinates && fullDetails.gps_coordinates.latitude),
            longitude: place.longitude || fullDetails.longitude || (fullDetails.gps_coordinates && fullDetails.gps_coordinates.longitude),
            gps_coordinates: fullDetails.gps_coordinates || place.gps_coordinates,
            
            // Contact & Rating
            rating: fullDetails.rating || place.rating || null,
            user_ratings_total: fullDetails.reviews || place.reviews || null,
            reviews: fullDetails.reviews || place.reviews || null,
            phone: fullDetails.phone || place.phone || null,
            
            // Business details
            website: place.website || fullDetails.website || place.link || null,
            link: place.link || fullDetails.website || null,
            domain: fullDetails.domain || place.domain || null,
            
            // Categories - IMPORTANT: Extract from full_details
            categories: fullDetails.categories || place.categories || [],
            types: fullDetails.type ? [fullDetails.type] : (fullDetails.categories || []),
            type: fullDetails.type || fullDetails.category || place.type || null,
            category: fullDetails.category || null,
            primary_category: (fullDetails.categories && fullDetails.categories[0]) || 
                             fullDetails.category || 
                             fullDetails.type || 
                             (place.categories && place.categories[0]) || 
                             place.type || '',
            
            // Hours & Status
            hours: fullDetails.hours || place.hours || null,
            opening_hours: fullDetails.opening_hours || null,
            open_state: fullDetails.open_state || place.open_state || null,
            operating_hours: fullDetails.operating_hours || place.operating_hours || null,
            
            // Additional ValueSERP fields
            description: fullDetails.description || place.description || null,
            price: fullDetails.price || place.price || null,
            thumbnail: fullDetails.thumbnail || place.thumbnail || null,
            images: fullDetails.images || place.images || [],
            service_options: fullDetails.service_options || place.service_options || null,
            
            // Additional full_details fields
            address_breakdown: fullDetails.address_breakdown || null,
            wheelchair_accessibility: fullDetails.wheelchair_accessibility || null,
            topics: fullDetails.topics || null,
            knowledge_graph_id: fullDetails.knowledge_graph_id || null,
            business_availability_modes: fullDetails.business_availability_modes || null,
            rating_breakdown: fullDetails.rating_breakdown || null,
            
            // All raw data from ValueSERP
            raw_valueserp_data: response.data.data,
            full_details: fullDetails,
            
            // Source identifier
            isValueSerp: true,
            source: 'value_serp',
            
            // SAB detection - check both the original business object and full details
            isServiceAreaBusiness: this.isServiceAreaBusiness(fullDetails) || this.isServiceAreaBusiness(business)
          };
          
          // Update the search input to show the selected business name
          this.businessSearch = this.selectedBusiness.name;
          
          // Set search provider to value_serp for proper saving
          this.searchProvider = 'value_serp';
          
          // Re-enable Google autocomplete after ValueSERP selection
          this.enableGoogleAutocomplete();
          
          this.gridCenter = this.selectedBusiness.location;
          
          // Center map on business
          this.map.setCenter(this.selectedBusiness.location);
          this.map.setZoom(13);
          
          // Add business marker
          if (this.businessMarker) {
            this.businessMarker.setMap(null);
          }
          
          this.businessMarker = new window.google.maps.Marker({
            position: this.selectedBusiness.location,
            map: this.map,
            title: this.selectedBusiness.name,
            icon: this.getDraggableMarkerIcon(),
            draggable: true,
            animation: window.google.maps.Animation.DROP,
            zIndex: 1000
          });
          
          // Add drag listeners
          this.businessMarker.addListener('drag', (event) => {
            this.gridCenter = { 
              lat: event.latLng.lat(), 
              lng: event.latLng.lng() 
            };
            this.updateGridWhileDragging();
            this.updateDragLabel();
          });
          
          this.businessMarker.addListener('dragend', (event) => {
            this.gridCenter = { 
              lat: event.latLng.lat(), 
              lng: event.latLng.lng() 
            };
            this.selectedBusiness.location = this.gridCenter;
            this.drawGrid();
          });
          
          // Draw initial grid
          this.drawGrid();
          
          // Add drag label after grid is created
          this.createDragLabel();
        } else {
          console.error('Unexpected response structure:', response.data);
          // Fallback: use the basic business data we already have but capture ALL fields
          this.selectedBusiness = {
            // Basic info
            name: business.title || '',
            title: business.title || '',
            address: business.address || '',
            formatted_address: business.address || '',
            placeId: business.place_id || null,
            place_id: business.place_id || null,
            data_cid: business.data_cid,

            // Location fields
            country: business.country || 'United States',
            city: business.city || null,
            state: business.state || null,
            postal_code: business.postal_code || null,
            
            // Location
            location: {
              lat: parseFloat(business.latitude || business.lat || 0),
              lng: parseFloat(business.longitude || business.lng || 0)
            },
            latitude: business.latitude || business.lat,
            longitude: business.longitude || business.lng,
            
            // Contact & Rating
            rating: business.rating || null,
            user_ratings_total: business.reviews || null,
            reviews: business.reviews || null,
            phone: business.phone || null,
            
            // Business details
            website: business.website || business.link || null,
            link: business.link || null,
            
            // Categories
            categories: business.categories || [],
            type: business.type || null,
            primary_category: business.categories && business.categories[0] ? business.categories[0] : (business.type || ''),
            
            // Additional fields
            description: business.description || null,
            thumbnail: business.thumbnail || null,
            
            // All raw data
            raw_valueserp_data: business,
            
            // Source identifier
            isValueSerp: true,
            source: 'value_serp'
          };
          
          if (this.selectedBusiness.location.lat && this.selectedBusiness.location.lng) {
            this.gridCenter = this.selectedBusiness.location;
            this.map.setCenter(this.selectedBusiness.location);
            this.map.setZoom(13);
            
            if (this.businessMarker) {
              this.businessMarker.setMap(null);
            }
            
            this.businessMarker = new window.google.maps.Marker({
              position: this.selectedBusiness.location,
              map: this.map,
              title: this.selectedBusiness.name,
              icon: this.getDraggableMarkerIcon(),
              draggable: true,
              animation: window.google.maps.Animation.DROP,
              zIndex: 1000
            });
            
            this.businessMarker.addListener('drag', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.updateGridWhileDragging();
              this.updateDragLabel();
            });
            
            this.businessMarker.addListener('dragend', (event) => {
              this.gridCenter = { 
                lat: event.latLng.lat(), 
                lng: event.latLng.lng() 
              };
              this.selectedBusiness.location = this.gridCenter;
              this.drawGrid();
            });
            
            this.drawGrid();

            // Add drag label after grid is created
            this.createDragLabel();
          }
        }

        // Fetch keyword suggestions for the selected business (call for all scenarios)
        if (this.selectedBusiness) {
          this.fetchKeywordSuggestions();
        }
      } catch (error) {
        console.error('Error fetching Value SERP place details:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch business details. Please try again.'
        });
      } finally {
        this.valueSerpDetailLoading = false;
        // Clear the loading text from input
        if (this.selectedBusiness) {
          this.businessSearch = this.selectedBusiness.name;
        }
      }
    },

    // Helper methods to extract location components from Google Places
    extractCountryFromPlace(place) {
      if (!place.address_components) return null;
      const country = place.address_components.find(comp =>
        comp.types.includes('country')
      );
      return country ? country.long_name : null;
    },

    extractCityFromPlace(place) {
      if (!place.address_components) return null;
      const city = place.address_components.find(comp =>
        comp.types.includes('locality') || comp.types.includes('administrative_area_level_2')
      );
      return city ? city.long_name : null;
    },

    extractStateFromPlace(place) {
      if (!place.address_components) return null;
      const state = place.address_components.find(comp =>
        comp.types.includes('administrative_area_level_1')
      );
      return state ? state.short_name : null;
    },

    extractPostalCodeFromPlace(place) {
      if (!place.address_components) return null;
      const postalCode = place.address_components.find(comp =>
        comp.types.includes('postal_code')
      );
      return postalCode ? postalCode.long_name : null;
    }
  }
}
</script>

<style>
/* Remove ALL shadows from success modal on mobile but keep overlay */
@media (max-width: 768px) {
  .v-dialog .success-modal-card,
  .v-dialog .success-modal-card.v-card,
  .v-dialog .success-modal-card.v-sheet,
  .v-dialog__container .success-modal-card,
  .v-overlay__content .success-modal-card {
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
    -moz-box-shadow: none !important;
    border: none !important;
    outline: none !important;
  }

  /* Keep the overlay background but ensure no shadow on the card */
  .v-overlay__scrim {
    background-color: rgba(0, 0, 0, 0.46) !important; /* Keep default overlay */
  }

  /* Ensure the modal container has no shadow */
  .v-dialog > .v-overlay__content > div {
    box-shadow: none !important;
  }

  /* Remove shadow from dialog wrapper */
  .v-dialog__content > div {
    box-shadow: none !important;
  }

  /* Remove any elevation that might add shadow */
  .v-application .elevation-0,
  .v-application .elevation-1,
  .v-application .elevation-2,
  .v-application .elevation-24 {
    box-shadow: none !important;
  }
}

/* Global overrides for Vuetify containers to remove all padding/margin */
.container.lfp-container.fill-height.container--fluid {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  width: 100vw !important;
  height: 100vh !important;
}

.container.lfp-container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

.container--fluid {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

.fill-height {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
}
</style>

<style scoped>
/* Breadcrumb strip styles */
.breadcrumb-strip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  z-index: 5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.breadcrumb-item {
  font-size: 12px;
  background: linear-gradient(90deg, #6b7280 0%, #9ca3af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
}

.breadcrumb-item.clickable {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  transition: all 0.2s;
}

.breadcrumb-item.clickable:hover {
  background: linear-gradient(90deg, #374151 0%, #4b5563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: underline;
}

.breadcrumb-item.current {
  background: linear-gradient(90deg, #374151 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 6px;
  color: #9ca3af;
  font-size: 14px;
}

/* Container with map taking full available space */
.create-scan-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.create-scan-container.with-breadcrumb {
  top: 30px;
  height: calc(100% - 30px);
}

/* Form overlay adjustment for embed mode with breadcrumb */
.create-scan-container.with-breadcrumb .form-overlay {
  height: calc(100% - 40px);
}

/* Full map without gaps */
.google-map-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}

/* Override Vuetify container styles */
.container.lfp-container,
.container--fluid,
 .fill-height {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  height: 100% !important;
}

/* Deep selector for scoped styles */
::v-deep .container.lfp-container {
  padding: 0 !important;
  margin: 0 !important;
}

::v-deep .container--fluid {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

::v-deep .fill-height {
  height: 100% !important;
}

/* Search overlay - top left */
.search-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 40; /* Lower than mobile header (50) */
  max-width: calc(100% - 40px); /* Prevent overflow on mobile */
}

.search-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  width: 450px;
  max-width: 100%; /* Ensure it doesn't overflow on mobile */
}

.search-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;
}

.search-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #19233A;
  margin-bottom: 8px;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 12px 85px 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #1a1a1a;
  background: white;
  transition: all 0.2s;
}

.value-serp-search-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px 16px;
  background: #2477F6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% - 8px);
}

.value-serp-search-btn:hover:not(:disabled) {
  background: #1e5fc6;
}

.value-serp-search-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.value-serp-search-btn.loading {
  background: #1e5fc6;
}

.spinner {
  animation: rotate 1s linear infinite;
}

.spinner.large {
  margin-right: 8px;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinner .path {
  stroke: white;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.value-serp-dropdown.loading .spinner .path {
  stroke: #2477F6;
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.value-serp-results-container {
  position: relative;
  margin-top: 8px;
}

.value-serp-dropdown {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.close-dropdown {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.close-dropdown:hover {
  color: #374151;
}

.dropdown-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.hovered {
  background: #f3f4f6;
}

.business-icon {
  color: #ef4444;
  margin-right: 12px;
  font-size: 13px;
}

.business-info {
  flex: 1;
}

/* Remove generic styles that were conflicting with ValueSERP dropdown */

.loading-state {
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.value-serp-dropdown.loading .loading-state {
  flex-direction: row;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.no-results i {
  font-size: 24px;
  color: #9ca3af;
}

.search-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border-radius: 4px;
  font-size: 12px;
  color: #1e40af;
}

.search-hint i {
  font-size: 14px;
}

.search-input::placeholder {
  font-size: 13px;
}

.search-input:focus {
  outline: none;
  border-color: #2477f6;
  box-shadow: 0 0 0 3px rgba(36, 119, 246, 0.1);
}

.search-input:disabled {
  background-color: #f8f9fa;
  cursor: wait;
  color: #666;
}

/* Form overlay - left side */
.form-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  bottom: 20px;
  z-index: 40; /* Lower than mobile header (50) */
  width: 450px;
  max-width: calc(100vw - 40px);
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.form-card {
  background: white;
  border-radius: 12px;
  padding: clamp(12px, 2vh, 24px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.15);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* Target Business Section */
.target-business-section {
  margin-bottom: clamp(12px, 2.5vh, 24px);
  flex-shrink: 0;
}

.target-business-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(8px, 1.5vh, 16px);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.target-business-title {
  font-size: clamp(12px, 1.4vh, 14px);
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.collapsed-business-name {
  font-size: clamp(11px, 1.3vh, 13px);
  color: #6b7280;
  font-weight: 400;
}

.edit-business-btn {
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
}

.edit-business-btn:hover {
  opacity: 0.7;
}

/* Mobile Toggle Button */
.mobile-toggle-btn {
  display: none; /* Hidden by default, shown on mobile */
  padding: 6px 8px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  font-size: 16px;
}

.mobile-toggle-btn:hover {
  opacity: 0.7;
  background: #f3f4f6;
  border-radius: 4px;
}

.mobile-toggle-btn i {
  transition: transform 0.3s ease;
}

/* Collapsible form content */
.form-content {
  transition: all 0.3s ease;
}

.business-details-box {
  border: 1px solid #e5e7eb;
  border-radius: clamp(6px, 1vh, 8px);
  padding: clamp(10px, 1.5vh, 16px);
}

.business-name {
  font-size: clamp(14px, 1.8vh, 18px);
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 clamp(8px, 1.2vh, 12px) 0;
}

.business-meta {
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.8vh, 8px);
}

.rating-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #d1d5db;
  font-size: clamp(12px, 1.4vh, 14px);
}

.star.filled {
  color: #fbbf24;
}

.rating-value {
  font-size: clamp(12px, 1.4vh, 14px);
  color: #19233A99;
}

.contact-info,
.address-info {
  font-size: clamp(12px, 1.4vh, 14px);
  color: #19233A;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.status-badge.open {
  background: #dcfce7;
  color: #166534;
}

.status-badge.closed {
  background: #fee2e2;
  color: #991b1b;
}

/* Form Sections */
.form-section {
  margin-bottom: clamp(12px, 2.5vh, 24px);
  flex-shrink: 0;
}

.section-header {
  font-size: clamp(12px, 1.4vh, 14px);
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 clamp(8px, 1.5vh, 16px) 0;
}

/* Keyword Section with Border */
.keyword-section {
  border: 1px solid #e5e7eb;
  border-radius: clamp(6px, 1vh, 8px);
  padding: clamp(10px, 1.5vh, 16px);
  margin-bottom: clamp(12px, 2vh, 20px);
}

.form-field {
  margin-bottom: clamp(10px, 1.5vh, 16px);
}

.field-label {
  display: block;
  font-size: clamp(11px, 1.2vh, 12px);
  font-weight: 500;
  color: #6b7280;
  margin-bottom: clamp(6px, 0.8vh, 8px);
}

.field-input,
.field-select {
  width: 100%;
  padding: clamp(8px, 1vh, 10px) clamp(10px, 1.2vh, 12px);
  border: 1px solid #e5e7eb;
  border-radius: clamp(4px, 0.6vh, 6px);
  font-size: clamp(12px, 1.4vh, 14px);
  color: #1a1a1a;
  background: white;
  transition: all 0.2s;
}

.field-input:focus,
.field-select:focus {
  outline: none;
  border-color: #2477f6;
  box-shadow: 0 0 0 3px rgba(36, 119, 246, 0.1);
}

.field-hint {
  display: block;
  margin-top: clamp(4px, 0.6vh, 6px);
  font-size: clamp(10px, 1.1vh, 11px);
  color: #9ca3af;
}

.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(6px, 0.8vh, 8px);
  margin-top: clamp(8px, 1.2vh, 12px);
}

.keyword-tag {
  display: inline-flex;
  align-items: center;
  gap: clamp(4px, 0.6vh, 6px);
  padding: clamp(4px, 0.6vh, 6px) clamp(8px, 1.2vh, 12px);
  background: #eff6ff;
  color: #1e40af;
  border-radius: clamp(3px, 0.4vh, 4px);
  font-size: clamp(11px, 1.3vh, 13px);
}

.remove-tag {
  cursor: pointer;
  font-size: 16px;
  color: #6b7280;
}

.remove-tag:hover {
  color: #dc2626;
}

/* Suggested Keywords Section */
.suggested-keywords-section {
  margin-top: clamp(10px, 1.5vh, 16px);
  padding-top: clamp(10px, 1.5vh, 16px);
  border-top: 1px solid #e5e7eb;
}

.suggested-keywords-header {
  display: flex;
  align-items: center;
  gap: clamp(6px, 0.8vh, 8px);
  margin-bottom: clamp(8px, 1.2vh, 12px);
}

.suggested-label {
  font-size: clamp(11px, 1.3vh, 13px);
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.suggested-keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(6px, 0.8vh, 8px);
}

.suggested-keyword-tag {
  display: inline-flex;
  align-items: center;
  gap: clamp(3px, 0.4vh, 4px);
  padding: clamp(4px, 0.6vh, 6px) clamp(8px, 1.2vh, 12px);
  border: 1px solid #19233A0D;
  border-radius: clamp(16px, 2vh, 20px);
  font-size: clamp(11px, 1.3vh, 13px);
  cursor: pointer;
  transition: all 0.2s;
  background: #19233A0D;
  color: #19233A;
}

.suggested-keyword-tag:hover {
  background: #dbeafe;
  border-color: #93c5fd;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.suggested-keyword-tag .add-icon {
  font-size: 16px;
  color: #19233A;
}

.suggested-keyword-tag.more-button {
  background: #2477f6;
  color: white;
  border-color: #2477f6;
  font-weight: 500;
}

.suggested-keyword-tag.more-button:hover {
  background: #1d66d9;
  border-color: #1d66d9;
  color: white;
}

.spinner.small {
  animation: spin 1s linear infinite;
}

/* Form Row */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(10px, 1.5vh, 16px);
  margin-bottom: clamp(12px, 2vh, 20px);
}

/* Map Grid Section with Border - All controls inside */
.map-grid-section {
  border: 1px solid #e5e7eb;
  border-radius: clamp(6px, 1vh, 8px);
  padding: clamp(10px, 1.5vh, 16px);
  margin-bottom: clamp(12px, 2.5vh, 24px);
}
.schedule-grid-section {
  border: 1px solid #e5e7eb;
  border-radius: clamp(6px, 1vh, 8px);
  padding: clamp(10px, 1.5vh, 16px);
  margin-bottom: clamp(12px, 2.5vh, 24px);
}
/* Radius Row - 30% toggle, 70% dropdown */
.radius-row {
  display: grid;
  grid-template-columns: 30% 70%;
  gap: clamp(10px, 1.5vh, 16px);
  align-items: flex-end;
  margin-bottom: clamp(10px, 1.5vh, 16px);
}

/* Unit Toggle Container */
.unit-toggle-container {
  display: flex;
  align-items: center;
}

/* Segmented Toggle - Like form.png */
.segmented-toggle {
  display: flex;
  background: #f3f4f6;
  border-radius: clamp(4px, 0.6vh, 6px);
  padding: 2px;
  width: 100%;
}

.toggle-segment {
  flex: 1;
  padding: clamp(6px, 0.8vh, 8px) clamp(8px, 1.2vh, 12px);
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: clamp(11px, 1.3vh, 13px);
  font-weight: 600;
  cursor: pointer;
  border-radius: clamp(3px, 0.4vh, 4px);
  transition: all 0.2s;
  text-align: center;
  outline: none;
}

.toggle-segment.active {
  background: #2477f6;
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-segment:not(.active):hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Radius Select Container */
.radius-select-container {
  display: flex;
  flex-direction: column;
}

/* Location Info Row */
.location-info-row {
  text-align: center;
  padding-top: clamp(6px, 0.8vh, 8px);
}

.location-count {
  font-size: clamp(11px, 1.3vh, 13px);
  color: #6b7280;
}

/* Recurring Fields */
.recurring-fields {
  margin-top: clamp(10px, 1.5vh, 16px);
}

/* Enable All Message */
.enable-all-message {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #19233A;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.enable-all-message:hover {
  background-color: #2c3650;
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1);
}

/* Submit Button */
.submit-button {
  width: 100%;
  padding: clamp(10px, 1.4vh, 14px);
  background: #2477F6;
  color: white;
  border: none;
  border-radius: clamp(4px, 0.6vh, 6px);
  font-size: clamp(12px, 1.4vh, 14px);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  margin-top: auto;
  flex-shrink: 0;
}

.submit-button:hover:not(:disabled) {
  background: #1d5fd4;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Success Modal - Matching backend/popup.png */
.success-modal-card {
  border-radius: 16px !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
}

/* Lighter overlay for modal - Desktop only */
@media (min-width: 769px) {
  ::v-deep .v-overlay--active {
    background-color: rgba(0, 0, 0, 0.3) !important; /* Lighter overlay */
  }

  ::v-deep .v-dialog__content--active {
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
  }
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 10;
}

.success-icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 10px;
}

.success-icon {
  display: block;
  margin: 0 auto;
}

.success-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 16px !important;
  line-height: 1.2;
}

.success-message {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 32px !important;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

.action-btn {
  min-width: 130px !important;
  height: 44px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.25px;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
}

/* Outlined buttons - View History and Start New Scan */
.outlined-btn {
  border: 2px solid #2477F6 !important;
  color: #2477F6 !important;
  background: white !important;
  transition: all 0.3s ease !important;
}

.outlined-btn:hover {
  background: #2477F6 !important;
  color: white !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(36, 119, 246, 0.25) !important;
}

/* Primary button - View Grid only */
.primary-btn {
  background-color: #2477F6 !important;
  color: white !important;
  border: 2px solid #2477F6 !important;
  transition: all 0.3s ease !important;
}

.primary-btn:hover {
  background-color: #1d5fd4 !important;
  border-color: #1d5fd4 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(36, 119, 246, 0.35) !important;
}

/* Search Mode Section */
.search-mode-section {
  margin: 15px 0;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
}

.search-mode-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.search-mode-label {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* SAB Toggle Styles */
.sab-toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background-color: #ccc;
  border-radius: 12px;
  transition: background-color 0.3s;
  margin-right: 10px;
}

.toggle-switch.active {
  background-color: #2477F6;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.toggle-switch.active .toggle-slider {
  transform: translateX(20px);
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.sab-hint {
  font-size: 11px;
  color: #999;
  font-style: italic;
  margin-top: 2px;
}

/* Input Loading Spinner */
.input-loading-spinner {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.spinner {
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: #2477F6;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 50;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 30, 50;
    stroke-dashoffset: -15;
  }
  100% {
    stroke-dasharray: 30, 50;
    stroke-dashoffset: -48;
  }
}

/* Value SERP Dropdown Styles */
.value-serp-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: -1px;
}

.value-serp-dropdown .loading-state {
  padding: 20px;
  text-align: center;
  color: #999;
}

.value-serp-dropdown .dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.value-serp-dropdown .dropdown-item:hover,
.value-serp-dropdown .dropdown-item.hovered {
  background-color: #f5f5f5;
}

.value-serp-dropdown .dropdown-item:last-child {
  border-bottom: none;
}

.value-serp-dropdown .business-line {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
}

.value-serp-dropdown .business-title {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 6px;
}

.value-serp-dropdown .business-address {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.value-serp-dropdown .no-results {
  padding: 12px;
  text-align: center;
  color: #666;
  font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .search-overlay {
    left: 10px;
    right: 10px;
    max-width: calc(100% - 20px);
    top: 70px; /* Account for mobile header */
  }

  .form-overlay {
    left: 10px;
    right: 10px;
    max-width: calc(100% - 20px);
    top: 70px; /* Account for mobile header */
    width: calc(100% - 20px);
  }

  .search-card {
    width: 100%;
    max-width: 100%;
    padding: 16px;
  }

  .form-card {
    width: 100%;
    padding: 16px;
  }

  /* Show mobile toggle button on mobile */
  .mobile-toggle-btn {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  /* Make form sections more compact on mobile */
  .form-section {
    margin-bottom: 16px;
  }

  .target-business-section {
    margin-bottom: 16px;
  }

  .business-details-box {
    padding: 12px;
  }

  .business-name {
    font-size: 16px;
  }

  /* Adjust grid controls on mobile */
  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .radius-row {
    grid-template-columns: 40% 60%;
    gap: 12px;
  }

  /* Search input container responsive */
  .search-input-container {
    width: 100%;
  }

  .search-input {
    padding: 10px 80px 10px 12px; /* Adjust padding for search button */
    font-size: 14px;
  }

  .value-serp-search-btn {
    padding: 6px 12px;
    font-size: 12px;
  }

  /* ValueSERP dropdown responsive */
  .value-serp-dropdown {
    max-width: 100%;
    left: 0;
    right: 0;
  }

  .dropdown-item {
    padding: 10px;
  }

  .business-line {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .business-title {
    flex-shrink: initial;
    white-space: normal;
  }

  .business-address {
    white-space: normal;
    text-overflow: initial;
  }

  /* Submit button */
  .submit-button {
    padding: 12px;
    font-size: 13px;
  }

  /* Suggested keywords */
  .suggested-keyword-tag {
    padding: 4px 10px;
    font-size: 12px;
  }

  /* Map grid section */
  .map-grid-section {
    padding: 12px;
  }

  .keyword-section {
    padding: 12px;
  }

  /* Success Modal Mobile Styles */
  .success-modal-card {
    margin: 16px !important;
    max-width: calc(100% - 32px) !important;
    box-shadow: none !important; /* No shadow on mobile */
  }

  /* Override all possible shadow sources on mobile */
  .v-dialog .success-modal-card {
    box-shadow: none !important;
  }

  .v-dialog__content .success-modal-card {
    box-shadow: none !important;
  }

  /* Override Vuetify v-card default shadows */
  .success-modal-card.v-card {
    box-shadow: none !important;
  }

  .success-modal-card.v-sheet {
    box-shadow: none !important;
  }

  /* Remove elevation shadows from Vuetify */
  .success-modal-card[class*="elevation-"] {
    box-shadow: none !important;
  }

  .success-modal-card .v-card__text {
    padding: 24px 16px !important;
  }

  .success-icon-container {
    margin-bottom: 16px;
    margin-top: 0;
  }

  .success-icon {
    width: 60px;
    height: 60px;
  }

  .success-title {
    font-size: 20px;
    margin-bottom: 12px !important;
  }

  .success-message {
    font-size: 14px;
    margin-bottom: 24px !important;
    padding: 0 8px;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
    gap: 10px;
    margin-top: 16px;
    padding: 0 8px;
  }

  .action-btn {
    width: 100% !important;
    min-width: auto !important;
    height: 42px !important;
    font-size: 13px !important;
  }

  /* Make primary button more prominent on mobile */
  .primary-btn {
    order: -1; /* Move to top */
  }

  /* Adjust close button position */
  .close-btn {
    right: 8px;
    top: 8px;
  }
}
</style>

<!-- Global styles to remove container padding -->
<style>
/* Remove all padding/margin from Vuetify containers on this page */
.lfp-container.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

.container--fluid {
  padding: 0 !important;
  margin: 0 !important;
}

.fill-height {
  height: 100vh !important;
}

/* Ensure navigation content area has no padding */
.v-main__wrap {
  padding: 0 !important;
}

.v-content__wrap {
  padding: 0 !important;
}
</style>